import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useDataSource } from '../contexts/DataSourceContext';
import { useDataMode } from '../contexts/DataModeContext';
import type { DataMode } from '../contexts/DataModeContext';
import { ScannerData } from '../types/ScannerData';
import { formatDate } from '../utils/dateUtils';
import { getExpiryFromRecord, legacyUnderlyingSelectionMatches } from '../utils/filterUtils';
import TrackButton from './TrackButton';
import { matchesCategoryFilter } from '../utils/symbolCategoryUtils';
import { dataService } from '../services/dataService';
import { readStablePrimarySessionFirst, readStableUnderlyingsSessionFirst, setStableTabSelection } from '../utils/stableTabStorage';
import { useHistoricalModal } from '../contexts/HistoricalModalContext';
import { columnKeyToHistoricalSource } from '../utils/dataTableHistoricalSource';
import { openContractInAnalyticsHub, historicalParamsFromScannerRow } from '../utils/analyticsHubNavigation';
import { DataTableConsolidatedView, type ConsolidatedGroupBy } from './DataTableConsolidatedView';
import { mmecSimpleCloseBtnStyle, mmecSimpleFabStyle, MMEC_SIMPLE_SCROLL_TABLE_WRAP_CLASS } from '../theme/mmecSimpleMobile';
import { ProToolFilterSlot } from './mmec/ProToolFilterSlot';
import { randomClientIdSuffix } from '../utils/pageIdUtils';
import {
  getSimpleTableDensityMetrics,
  loadSimpleScannerTableDensity,
  saveSimpleScannerTableDensity,
  type SimpleScannerTableDensity,
} from '../utils/simpleScannerTableDensity';
import {
  COLUMN_WIDTH_CATALOG_DEFAULT,
  SCANNER_COLUMN_PRESET_WIDTH,
  estimateScannerColumnContentWidths,
} from '../utils/dataTableColumnAutoWidth';
import { sessionPhase, volumeAsOfNote, OPEN_INTEREST_NOTE } from '../utils/sessionAsOf';
import { contractPremiumFields } from '../utils/contractPremiumPool';
import { ToolState } from './mmec/ToolState';
import { isDesignSystemOn } from '../theme/mmecDsPreview';
import './DataTable.ds.css';

/** Columns whose cells hold text; every other column is numeric and right-aligned (header and body). */
const DATA_TABLE_TEXT_COLUMN_KEYS = new Set([
  'Underlying', 'Option Type', 'Expiry Date', 'Symbol', 'Direction', 'Sentiment', 'Moneyness',
  'Direction accumelated', 'Sentiment accumelated',
]);
const isDataTableTextColumn = (key: string) => DATA_TABLE_TEXT_COLUMN_KEYS.has(key);

// Memoized Table Row Component for better performance with large datasets
interface TableRowProps {
  item: ScannerData;
  rowIndex: number;
  frozenColumns: any[];
  scrollableColumns: any[];
  /** Cumulative `left` offset in px for each frozen column (horizontal sticky). */
  frozenStickyLefts: number[];
  getColumnDisplayWidth: (col: { width?: number; key: string }) => number;
  getCellValue: (item: ScannerData, key: string) => any;
  getCellStyle: (item: ScannerData, key: string, value: any) => any;
  getHighLowStyle: (key: string, value: number) => any;
  themeColors: any;
  isDarkMode: boolean;
  /** Bumps row memo when simple-mode density changes (getCellStyle is not referentially stable). */
  simpleLayoutDensityKey?: string | null;
  onCellClick?: (item: ScannerData, columnKey: string) => void;
  onCellContextMenu?: (item: ScannerData, columnKey: string) => void;
  /** Track button cell rendered as the last column */
  trackCell?: React.ReactNode;
}

const TableRow = memo(({ 
  item, 
  rowIndex, 
  frozenColumns, 
  scrollableColumns,
  frozenStickyLefts,
  getColumnDisplayWidth,
  getCellValue, 
  getCellStyle, 
  getHighLowStyle, 
  themeColors,
  isDarkMode,
  simpleLayoutDensityKey,
  onCellClick,
  onCellContextMenu,
  trackCell,
}: TableRowProps) => (
  <tr
    className={`data-table-mmec-row transition-colors duration-200 ${onCellClick ? 'cursor-pointer' : ''}`}
    style={{ backgroundColor: rowIndex % 2 === 0 ? themeColors.tableStripeEven : themeColors.tableStripeOdd }}
  >
    {/* Frozen Columns */}
    {frozenColumns.map((col: any, fi: number) => {
      const value = getCellValue(item, col.key);
      const cellStyle = getCellStyle(item, col.key, value);
      const highLowStyle = getHighLowStyle(col.key, item[col.key as keyof ScannerData] as number);
      const leftPx = frozenStickyLefts[fi] ?? 0;
      const cw = getColumnDisplayWidth(col);
      
      return (
        <td
          key={col.key}
          className="border-r-2"
          style={{
            ...cellStyle,
            ...highLowStyle,
            position: 'sticky',
            left: leftPx,
            zIndex: 12,
            boxSizing: 'border-box',
            borderRightColor: themeColors.frozenBorder,
            backgroundColor: cellStyle.backgroundColor || highLowStyle.backgroundColor || (rowIndex % 2 === 0 ? themeColors.tableStripeEven : themeColors.tableStripeOdd),
            minWidth: cw,
            maxWidth: cw,
            width: cw,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onCellClick?.(item, col.key);
          }}
          onContextMenu={(e) => {
            if (!onCellContextMenu) return;
            e.preventDefault();
            e.stopPropagation();
            onCellContextMenu(item, col.key);
          }}
        >
          {value}
        </td>
      );
    })}
    
    {/* Scrollable Columns */}
    {scrollableColumns.map((col: any) => {
      const value = getCellValue(item, col.key);
      const cellStyle = getCellStyle(item, col.key, value);
      const highLowStyle = getHighLowStyle(col.key, item[col.key as keyof ScannerData] as number);
      const cw = getColumnDisplayWidth(col);
      
      return (
        <td
          key={col.key}
          className="border-r"
          style={{
            ...cellStyle,
            ...highLowStyle,
            boxSizing: 'border-box',
            borderColor: themeColors.border,
            backgroundColor: cellStyle.backgroundColor || highLowStyle.backgroundColor || (rowIndex % 2 === 0 ? themeColors.tableStripeEven : themeColors.tableStripeOdd),
            minWidth: cw,
            maxWidth: cw,
            width: cw,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onCellClick?.(item, col.key);
          }}
          onContextMenu={(e) => {
            if (!onCellContextMenu) return;
            e.preventDefault();
            e.stopPropagation();
            onCellContextMenu(item, col.key);
          }}
        >
          {value}
        </td>
      );
    })}
    {trackCell}
  </tr>
), (prevProps, nextProps) => {
  // Custom comparison for memo - only re-render if these change
  return prevProps.item === nextProps.item && 
         prevProps.rowIndex === nextProps.rowIndex &&
         prevProps.isDarkMode === nextProps.isDarkMode &&
         prevProps.onCellClick === nextProps.onCellClick &&
         prevProps.onCellContextMenu === nextProps.onCellContextMenu &&
         prevProps.frozenStickyLefts === nextProps.frozenStickyLefts &&
         prevProps.getColumnDisplayWidth === nextProps.getColumnDisplayWidth &&
         prevProps.simpleLayoutDensityKey === nextProps.simpleLayoutDensityKey &&
         prevProps.themeColors.tableStripeEven === nextProps.themeColors.tableStripeEven &&
         prevProps.themeColors.tableStripeOdd === nextProps.themeColors.tableStripeOdd &&
         prevProps.themeColors.rowHover === nextProps.themeColors.rowHover &&
         prevProps.themeColors.border === nextProps.themeColors.border &&
         prevProps.themeColors.frozenBorder === nextProps.themeColors.frozenBorder &&
         prevProps.trackCell === nextProps.trackCell;
});

// Helper function to calculate moneyness category from strike and underlying price
const calculateMoneynessCategoryFromPrice = (
  strike: number,
  underlyingPrice: number,
  optionType: string
): 'ITM' | 'ATM' | 'OTM' => {
  const ratio = strike / underlyingPrice;
  const normalizedOptionType = optionType === 'C' ? 'C' : optionType === 'P' ? 'P' : optionType;
  
  if (normalizedOptionType === 'C' || normalizedOptionType === 'CALL') {
    // For calls: ITM when strike < underlying price, OTM when strike > underlying price
    if (ratio < 0.95) return 'ITM';  // Strike significantly below underlying
    if (ratio > 1.05) return 'OTM'; // Strike significantly above underlying
    return 'ATM'; // Near the money
  } else if (normalizedOptionType === 'P' || normalizedOptionType === 'PUT') {
    // For puts: ITM when strike > underlying price, OTM when strike < underlying price
    if (ratio > 1.05) return 'ITM'; // Strike significantly above underlying
    if (ratio < 0.95) return 'OTM'; // Strike significantly below underlying
    return 'ATM'; // Near the money
  }
  
  return 'OTM'; // Default fallback
};

// Available alert sounds - Using MP3 files from root directory
const ALERT_SOUNDS = [
  { id: 'bell-notification-337658', name: 'Bell Notification', filename: 'bell-notification-337658.mp3', description: 'Classic bell notification sound' },
  { id: 'bell-sound-370341', name: 'Bell Sound', filename: 'bell-sound-370341.mp3', description: 'Clear bell sound' },
  { id: 'level-up-02-199574', name: 'Level Up', filename: 'level-up-02-199574.mp3', description: 'Achievement sound' },
  { id: 'new-notification-08-352461', name: 'New Notification', filename: 'new-notification-08-352461.mp3', description: 'Modern notification sound' },
  { id: 'new-notification-09-352705', name: 'New Notification 2', filename: 'new-notification-09-352705.mp3', description: 'Alternative notification sound' },
  { id: 'notification-bell-sound-376888', name: 'Notification Bell', filename: 'notification-bell-sound-376888.mp3', description: 'Professional notification bell' },
  { id: 'none', name: 'No Sound', filename: '', description: 'Silent alerts' }
];

interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  order: number;
  frozen?: boolean;
  width?: number;
}

/**
 * Only these keys may be horizontally sticky (pro + simple). Extra `frozen` flags in saved prefs
 * are ignored so Expiry / Days / Last / etc. always scroll with the wide grid.
 */
const SCANNER_STICKY_COLUMN_KEYS = new Set<string>(['Underlying', 'Option Type', 'Strike']);

function columnIsStickyInLayout(col: ColumnConfig): boolean {
  if (!col.frozen) return false;
  return SCANNER_STICKY_COLUMN_KEYS.has(col.key);
}

interface FilterCondition {
  id: string;
  field: keyof ScannerData | string;
  operator: 'equals' | 'contains' | 'greater' | 'less' | 'between' | 'in' | 'not_equals' | 'not_contains' | 'is_null' | 'is_not_null';
  value: string | number | [number, number] | string[];
  logicalOperator: 'AND' | 'OR';
}

interface FilterGroup {
  id: string;
  conditions: FilterCondition[];
  logicalOperator: 'AND' | 'OR';
}

function cloneFilterGroups(groups: FilterGroup[]): FilterGroup[] {
  return groups.map((g) => ({
    ...g,
    conditions: g.conditions.map((c) => ({ ...c })),
  }));
}

/** Raw value for filter matching (numeric days for expiry-based fields). */
function getFilterFieldValue(item: ScannerData, field: string): unknown {
  const key = field.trim();
  if (key === 'Days left' || key === 'Days Left') {
    const expiryStr = getExpiryFromRecord(item) || item['Expiry Date'];
    if (expiryStr) {
      try {
        const expiry = new Date(expiryStr);
        const today = new Date();
        const expMs = expiry.getTime();
        const todayMs = today.getTime();
        if (Number.isFinite(expMs) && Number.isFinite(todayMs)) {
          return Math.ceil((expMs - todayMs) / (1000 * 60 * 60 * 24));
        }
      } catch {
        /* use backend field */
      }
    }
    const raw = item['Days left'];
    if (typeof raw === 'number' && Number.isFinite(raw)) return Math.round(raw);
    const parsed = parseFloat(String(raw ?? '').replace(/,/g, ''));
    if (Number.isFinite(parsed)) return Math.round(parsed);
    return null;
  }
  return item[key as keyof ScannerData];
}

function isOptionTypeFieldKey(field: string): boolean {
  const f = field.trim().toLowerCase();
  return f === 'option type' || f === 'side' || f === 'optiontype';
}

/** Normalize C/P vs CALL/PUT so advanced filters match scanner rows. */
function normalizeOptionSideForFilter(v: unknown): string {
  const s = String(v ?? '').trim().toUpperCase();
  if (s === 'C' || s === 'CALL' || s === 'CALLS') return 'CALL';
  if (s === 'P' || s === 'PUT' || s === 'PUTS') return 'PUT';
  return s;
}

function filterEquals(field: string, itemValue: unknown, filterValue: unknown): boolean {
  const f = field.trim();
  if (isOptionTypeFieldKey(f)) {
    return normalizeOptionSideForFilter(itemValue) === normalizeOptionSideForFilter(filterValue);
  }
  const numItem = (v: unknown) => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = parseFloat(String(v ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : NaN;
  };
  const na = numItem(itemValue);
  const nb = numItem(filterValue);
  const fs = String(filterValue ?? '').trim();
  if (fs !== '' && Number.isFinite(na) && Number.isFinite(nb)) {
    return na === nb;
  }
  return String(itemValue ?? '').trim().toUpperCase() === String(filterValue ?? '').trim().toUpperCase();
}

/** True when this condition should not exclude any rows (empty / incomplete criterion). */
function advancedConditionHasNoCriterion(condition: FilterCondition): boolean {
  if (!String(condition.field ?? '').trim()) return true;
  const { operator, value } = condition;
  if (operator === 'is_null' || operator === 'is_not_null') return false;

  if (operator === 'between') {
    const loHi = parseBetweenBounds(value);
    return loHi === null;
  }

  if (operator === 'in') {
    const tokens = tokenizeInList(value);
    return tokens.length === 0;
  }

  return String(value ?? '').trim() === '';
}

function parseBetweenBounds(value: FilterCondition['value']): [number, number] | null {
  if (Array.isArray(value) && value.length === 2) {
    const lo = parseFloat(String(value[0]).replace(/,/g, ''));
    const hi = parseFloat(String(value[1]).replace(/,/g, ''));
    if (Number.isFinite(lo) && Number.isFinite(hi)) return [lo, hi];
    return null;
  }
  if (typeof value === 'string' && value.includes(',')) {
    const parts = value.split(',').map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const lo = parseFloat(parts[0].replace(/,/g, ''));
      const hi = parseFloat(parts[1].replace(/,/g, ''));
      if (Number.isFinite(lo) && Number.isFinite(hi)) return [lo, hi];
    }
  }
  return null;
}

function tokenizeInList(value: FilterCondition['value']): string[] {
  if (Array.isArray(value)) {
    return (value as unknown[]).map((v) => String(v).trim()).filter(Boolean);
  }
  return String(value ?? '')
    .split(/[,|;\n]/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

function evaluateFilterCondition(itemValue: unknown, condition: FilterCondition): boolean {
  if (advancedConditionHasNoCriterion(condition)) return true;

  const { operator, value } = condition;
  const fieldKey = String(condition.field ?? '');
  const numItem = (v: unknown) => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = parseFloat(String(v ?? '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : NaN;
  };

  switch (operator) {
    case 'equals':
      return filterEquals(fieldKey, itemValue, value);
    case 'not_equals':
      return !filterEquals(fieldKey, itemValue, value);
    case 'contains': {
      if (isOptionTypeFieldKey(fieldKey)) {
        const hay = normalizeOptionSideForFilter(itemValue).toLowerCase();
        const needle = normalizeOptionSideForFilter(value).toLowerCase();
        return hay.includes(needle);
      }
      return String(itemValue ?? '').toLowerCase().includes(String(value).toLowerCase());
    }
    case 'not_contains': {
      if (isOptionTypeFieldKey(fieldKey)) {
        const hay = normalizeOptionSideForFilter(itemValue).toLowerCase();
        const needle = normalizeOptionSideForFilter(value).toLowerCase();
        return !hay.includes(needle);
      }
      return !String(itemValue ?? '').toLowerCase().includes(String(value).toLowerCase());
    }
    case 'greater': {
      const a = numItem(itemValue);
      const b = numItem(value);
      if (Number.isFinite(a) && Number.isFinite(b)) return a > b;
      return String(itemValue) > String(value);
    }
    case 'less': {
      const a = numItem(itemValue);
      const b = numItem(value);
      if (Number.isFinite(a) && Number.isFinite(b)) return a < b;
      return String(itemValue) < String(value);
    }
    case 'between': {
      const bounds = parseBetweenBounds(value);
      if (!bounds) return true;
      const [lo, hi] = bounds;
      const numValue = numItem(itemValue);
      if (Number.isFinite(numValue)) return numValue >= lo && numValue <= hi;
      return false;
    }
    case 'in': {
      const tokens = tokenizeInList(value);
      if (isOptionTypeFieldKey(fieldKey)) {
        const iv = normalizeOptionSideForFilter(itemValue);
        return tokens.some((t) => normalizeOptionSideForFilter(t) === iv);
      }
      return tokens.map((t) => t.toLowerCase()).includes(String(itemValue).toLowerCase());
    }
    case 'is_null':
      return itemValue === null || itemValue === undefined || itemValue === '';
    case 'is_not_null':
      return itemValue !== null && itemValue !== undefined && itemValue !== '';
    default:
      return true;
  }
}

function buildDefaultColumns(
  language: string,
  isStable: boolean,
  rtlDataMode: DataMode = 'accumulated',
): ColumnConfig[] {
  const showDeltaStack = !isStable;
  const showVolOiPremium = isStable;

  const showRtlOiVolume = !isStable;
  const rtl5min = !isStable && rtlDataMode === '5min';
  const rtlAccumulated = !isStable && rtlDataMode === 'accumulated';
  const PW = SCANNER_COLUMN_PRESET_WIDTH;
  return [
    { key: 'Underlying', label: language === 'ar' ? 'الأصل الأساسي' : 'Underlying', visible: true, order: 1, frozen: true, width: PW.Underlying },
    { key: 'Option Type', label: language === 'ar' ? 'نوع الخيار' : 'Side', visible: true, order: 2, frozen: true, width: PW['Option Type'] },
    { key: 'Strike', label: language === 'ar' ? 'سعر التنفيذ' : 'Strike', visible: true, order: 3, frozen: true, width: PW.Strike },
    { key: 'Expiry Date', label: language === 'ar' ? 'تاريخ انتهاء الصلاحية' : 'Expiry Date', visible: true, order: 4, frozen: false, width: PW['Expiry Date'] },
    { key: 'Days left', label: language === 'ar' ? 'الأيام المتبقية' : 'Days Left', visible: true, order: 5, frozen: false, width: PW['Days left'] },
    { key: 'Last', label: language === 'ar' ? 'آخر سعر' : 'Last', visible: true, order: 6, frozen: false, width: PW.Last },
    { key: 'Delta ( ask - bid )', label: language === 'ar' ? 'دلتا (عرض-طلب)' : 'Delta (Ask-Bid)', visible: showDeltaStack && (rtl5min || (!rtl5min && !rtlAccumulated)), order: 7, width: PW['Delta ( ask - bid )'] },
    { key: 'Delta accumelated', label: language === 'ar' ? 'دلتا متراكم' : 'Delta ACC', visible: showDeltaStack && (rtlAccumulated || (!rtl5min && !rtlAccumulated)), order: 8, width: PW['Delta accumelated'] },
    { key: 'Delta Premium', label: language === 'ar' ? 'علاوة الدلتا' : 'Delta Premium', visible: showDeltaStack && rtl5min, order: 9, width: PW['Delta Premium'] },
    { key: 'Delta Accumulated Premium', label: language === 'ar' ? 'علاوة الدلتا المتراكمة' : 'Delta Acc Premium', visible: showDeltaStack && rtlAccumulated, order: 10, width: PW['Delta Accumulated Premium'] },
    { key: 'Zahn (Count Indicator)', label: language === 'ar' ? 'زان (العدد %)' : 'Zahn (Count %)', visible: showDeltaStack, order: 11, width: PW['Zahn (Count Indicator)'] },

    { key: 'Symbol', label: language === 'ar' ? 'الرمز' : 'Symbol', visible: false, order: 12, width: PW.Symbol },
    { key: 'Bid Count', label: language === 'ar' ? 'عدد الطلبات' : 'Bid Count', visible: false, order: 13, width: PW['Bid Count'] },
    { key: 'Ask Count', label: language === 'ar' ? 'عدد العروض' : 'Ask Count', visible: false, order: 14, width: PW['Ask Count'] },
    { key: 'Trade Count', label: language === 'ar' ? 'عدد الصفقات' : 'Trade Count', visible: rtl5min, order: 15, width: PW['Trade Count'] },
    { key: 'Bid Volume', label: language === 'ar' ? '🟣 حجم المباع' : '🟣 Sold Volume', visible: rtl5min, order: 16, width: PW['Bid Volume'] },
    { key: 'Ask Volume', label: language === 'ar' ? '🔵 حجم المشترى' : '🔵 Bought Volume', visible: rtl5min, order: 17, width: PW['Ask Volume'] },
    { key: 'Direction', label: language === 'ar' ? 'الاتجاه' : 'Direction', visible: false, order: 18, width: PW.Direction },
    { key: 'Sentiment', label: language === 'ar' ? 'المشاعر' : 'Sentiment', visible: false, order: 19, width: PW.Sentiment },
    { key: 'Moneyness', label: language === 'ar' ? 'النقدية' : 'Moneyness', visible: false, order: 20, width: PW.Moneyness },
    { key: 'Vol/OI', label: language === 'ar' ? 'الحجم/الفائدة' : 'Vol/OI', visible: false, order: 21, width: PW['Vol/OI'] },
    { key: 'Mid', label: language === 'ar' ? 'الوسط' : 'Mid', visible: false, order: 22, width: PW.Mid },
    { key: 'Volume', label: language === 'ar' ? 'الحجم' : 'Volume', visible: showVolOiPremium, order: 23, width: PW.Volume },
    { key: 'Volume D-1', label: language === 'ar' ? 'الحجم D-1' : 'Volume D-1', visible: false, order: 23.1, width: PW['Volume D-1'] },
    { key: 'Volume D-2', label: language === 'ar' ? 'الحجم D-2' : 'Volume D-2', visible: false, order: 23.2, width: PW['Volume D-2'] },
    { key: 'Volume D-3', label: language === 'ar' ? 'الحجم D-3' : 'Volume D-3', visible: false, order: 23.3, width: PW['Volume D-3'] },
    { key: 'Volume D-4', label: language === 'ar' ? 'الحجم D-4' : 'Volume D-4', visible: false, order: 23.4, width: PW['Volume D-4'] },
    { key: 'Open Interest', label: language === 'ar' ? 'الفائدة المفتوحة' : 'Open Interest', visible: showVolOiPremium || showRtlOiVolume, order: 24, width: PW['Open Interest'] },
    { key: 'Implied Volatility', label: language === 'ar' ? 'التقلب الضمني' : 'Implied Volatility', visible: false, order: 25, width: PW['Implied Volatility'] },
    { key: 'Underlying Price', label: language === 'ar' ? 'سعر الأصل' : 'Underlying Price', visible: false, order: 26, width: PW['Underlying Price'] },
    { key: 'Bid', label: language === 'ar' ? 'الطلب' : 'Bid', visible: false, order: 27, width: PW.Bid },
    { key: 'Ask', label: language === 'ar' ? 'العرض' : 'Ask', visible: false, order: 28, width: PW.Ask },
    { key: 'Total Volume', label: language === 'ar' ? 'الحجم الإجمالي' : 'Total Volume', visible: showRtlOiVolume, order: 29, width: PW['Total Volume'] },
    { key: 'Sentiment accumelated', label: language === 'ar' ? 'المشاعر المتراكمة' : 'Sentiment ACC', visible: false, order: 30, width: PW['Sentiment accumelated'] },
    { key: 'Direction accumelated', label: language === 'ar' ? 'الاتجاه المتراكم' : 'Direction ACC', visible: false, order: 31, width: PW['Direction accumelated'] },
    { key: 'Gamma', label: language === 'ar' ? 'جاما' : 'Gamma', visible: false, order: 32, width: PW.Gamma },

    { key: 'Bid Volume Premium', label: language === 'ar' ? 'علاوة حجم الطلب' : 'Bid Vol Premium', visible: false, order: 33, width: PW['Bid Volume Premium'] },
    { key: 'Ask Volume Premium', label: language === 'ar' ? 'علاوة حجم العرض' : 'Ask Vol Premium', visible: false, order: 34, width: PW['Ask Volume Premium'] },
    { key: 'Bid Accumulated Premium', label: language === 'ar' ? 'علاوة الطلب المتراكمة' : 'Bid Acc Premium', visible: false, order: 35, width: PW['Bid Accumulated Premium'] },
    { key: 'Ask Accumulated Premium', label: language === 'ar' ? 'علاوة العرض المتراكمة' : 'Ask Acc Premium', visible: false, order: 36, width: PW['Ask Accumulated Premium'] },

    // Stable only, and on by default there: all three need Open Interest, Volume and Last
    // together, which no other data source carries. Gating on `showVolOiPremium` is also
    // what puts them in the Stable preset, since that preset is this list filtered by
    // visibility.
    //
    // `Open Premium` is deliberately NOT a variant of `Overall Premium`. Overall Premium is
    // what TRADED today (volume x last x 100); this is what is SITTING in the contract --
    // every open contract plus today's volume, valued at last. Same units, different
    // question, so they are named apart rather than "premium" and "premium 2".
    { key: 'Contract Premium Pool', label: language === 'ar' ? 'العلاوة القائمة (مفتوحة + حجم)' : 'Open Premium (OI+Vol)', visible: showVolOiPremium, order: 44, width: PW['Contract Premium Pool'] },
    { key: 'Provably New Contracts', label: language === 'ar' ? 'عقود جديدة مؤكدة' : 'Proven New', visible: showVolOiPremium, order: 47, width: PW['Provably New Contracts'] },

    { key: 'Haltz (Volume Concentration)', label: language === 'ar' ? 'هالتز (نسبة الشراء/البيع)' : 'Haltz (Buy/Sell %)', visible: false, order: 37, width: PW['Haltz (Volume Concentration)'] },
    { key: 'Band (IV Indicator)', label: language === 'ar' ? 'باند (مؤشر التقلب %)' : 'Band (IV %)', visible: false, order: 38, width: PW['Band (IV Indicator)'] },
    { key: 'Mal (Gamma Indicator)', label: language === 'ar' ? 'مال (جاما %)' : 'Mal (Gamma %)', visible: false, order: 39, width: PW['Mal (Gamma Indicator)'] },

    { key: 'Rho', label: language === 'ar' ? 'رو' : 'Rho', visible: false, order: 40, width: PW.Rho },
    { key: 'Lambda', label: language === 'ar' ? 'لامدا' : 'Lambda', visible: false, order: 41, width: PW.Lambda },
    { key: 'Theoretical Value', label: language === 'ar' ? 'القيمة النظرية' : 'Theo Value', visible: false, order: 42, width: PW['Theoretical Value'] },
    { key: 'Overall Premium', label: language === 'ar' ? 'العلاوة الإجمالية' : 'Overall Premium', visible: showVolOiPremium, order: 43, width: PW['Overall Premium'] },
  ];
}

interface DataTableProps {
  data: ScannerData[];
  isLoading?: boolean;
  onDataUpdate?: (data: ScannerData[]) => void;
  /** When set (e.g. MMEC simple shell), any cell click reports underlying symbol instead of opening contract historical. */
  onRowClick?: (underlyingSymbol: string) => void;
  /** Simple shell: slide-out filters, compact toolbar, contract historical with mobile chrome. */
  simpleLayout?: boolean;
}

const DataTable: React.FC<DataTableProps> = ({ data, isLoading = false, onDataUpdate, onRowClick, simpleLayout = false }) => {
  const { isDarkMode, colors } = useTheme();
  const { language } = useLanguage();
  const { dataSource } = useDataSource();
  const { effectiveDataMode, setDataMode } = useDataMode();
  /** Tracks previous data source so we apply stable column preset once when entering Stable. */
  const prevDataSourceForColumnsRef = useRef(dataSource);
  /** Tracks previous RTL data mode so column preset re-applies on 5min ↔ accumulated. */
  const prevDataModeForColumnsRef = useRef<DataMode>(effectiveDataMode);
  /** Safe during HMR / circular init: `dataService` can be undefined before module finishes evaluating. */
  const lastLevel2Totals = dataService?.lastLevel2Totals ?? null;
  const isStableView = dataSource === 'stable';
  // Volume and Open Interest are not "now", and the board used to present them as if they
  // were. Open interest is published once after the close and describes the PREVIOUS
  // session; volume is this session's only once the session has started. Verified on
  // SPXW 7785C: pre-market the board showed volume 8,209 beside open interest 8,306 --
  // yesterday's volume next to the figure published that morning because of it.
  const marketState = sessionPhase();
  const volumeNote = volumeAsOfNote(marketState);
  const headerSuffix: Record<string, string> = (() => {
    const out: Record<string, string> = {};
    if (!isStableView) return out;
    out['Open Interest'] = '(prior close)';
    if (volumeNote) {
      out['Volume'] = '(prior session)';
      // The derived premium columns are now valued at the prior session's CLOSE, matching
      // the volume they multiply. `Overall Premium` still comes from the backend as
      // volume x live last, so it keeps the warning the others no longer need.
      out['Overall Premium'] = '(prior vol x live price)';
      out['Contract Premium Pool'] = '(prior session)';
    }
    return out;
  })();
  const headerNote: Record<string, string> = (() => {
    if (!isStableView) return {};
    const out: Record<string, string> = {
      'Open Interest': `Open interest is ${OPEN_INTEREST_NOTE}.`,
      'Overall Premium': 'Premium that TRADED this session: volume x last x 100.',
      'Contract Premium Pool':
        'Premium SITTING in the contract, not what traded: (open interest + volume) x last x 100. '
        + 'An approximation — the whole position is marked at today\'s last price, not at what was paid for it.',
      'Provably New Contracts':
        'Volume beyond open interest, which must be newly opened: closing a contract requires one to exist.',
    };
    if (volumeNote) {
      out['Volume'] = `Volume shown is ${volumeNote}.`;
      out['Overall Premium'] = (out['Overall Premium'] ?? '')
        + ' Outside regular hours this multiplies the PRIOR session\'s volume by the current'
        + ' live price, so it moves while nothing trades. It belongs to no session.';
      const atClose = ' Outside regular hours this is valued at the prior session\'s CLOSE,'
        + ' matching the volume it multiplies, so it does not drift with the live quote.';
      out['Contract Premium Pool'] = (out['Contract Premium Pool'] ?? '') + atClose;
    }
    return out;
  })();


  // Direct data reference
  // Client-derived premium fields, stamped onto the rows ONCE here rather than per cell.
  //
  // They first lived in utils/dataFieldMapper.ts, which turned out to be dead code -- nothing
  // imports it -- so the columns rendered empty. Computing them upstream of filtering and
  // sorting is also what makes those columns sortable: the comparator reads the raw field,
  // not the formatted cell.
  const deferredData = useMemo(() => {
    const rows = data || [];
    if (!isStableView || rows.length === 0) return rows;
    return rows.map((row) => {
      const p = contractPremiumFields({
        openInterest: row['Open Interest'],
        volume: row.Volume,
        last: row.Last,
        priorClose: (row as unknown as Record<string, unknown>)['Prior Close'],
        // Outside RTH the Volume on this row is the prior session's, so value it at that
        // session's close rather than at a live quote from a session that has not opened.
        usePriorClose: volumeNote !== null,
      });
      return {
        ...row,
        'Contract Premium Pool': p.pool,
        'Provably New Contracts': p.provablyNewContracts,
      } as ScannerData;
    });
  }, [data, isStableView]);
  const isDataStale = false; // No stale state - data is ready from backend
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100); // Increased default for better UX
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  
  // Simple filters — default to "all" for both RTL and Stable (fetching has its own filtering)
  const getAllDefaultsFilters = (): { [key: string]: string } => ({
    'Days Left': '',
    'Underlying': '',
    'Strike': '',
    'Option Type': '',
    'Moneyness': '',
    'Expiry Date': '',
  });
  const [filters, setFilters] = useState<{ [key: string]: string }>(getAllDefaultsFilters());
  const [stableSpecificExpiryDate, setStableSpecificExpiryDate] = useState(() =>
    typeof localStorage !== 'undefined' ? localStorage.getItem('mmec_stable_specific_expiry') || '' : ''
  );

  // Category filter state
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'stocks' | 'etfs' | 'indices'>('all');
  
  // Underlying selector state for performance - MULTI-SELECT
  // Legacy RTL: never hydrate from localStorage — stale symbols (e.g. after Stable) filter the table to zero rows.
  const [selectedUnderlyings, setSelectedUnderlyings] = useState<string[]>(() => {
    if (typeof localStorage === 'undefined') return [];
    const ds = localStorage.getItem('mmec_data_source') || 'rtl_legacy';
    if (ds === 'rtl_legacy' || ds === 'rtl') return [];
    return readStableUnderlyingsSessionFirst();
  });
  const [] = useState(false);
  const [apiUnderlyings, setApiUnderlyings] = useState<string[]>([]);
  const [underlyingInputValue, setUnderlyingInputValue] = useState('');
  
  // Advanced filtering system
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);
  const [useAdvancedFiltering, setUseAdvancedFiltering] = useState(false);
  /** Edit advanced rules in a centered modal; committed filterGroups update only on Done. */
  const [advancedFilterModalOpen, setAdvancedFilterModalOpen] = useState(false);
  const [advancedFilterDraft, setAdvancedFilterDraft] = useState<FilterGroup[]>([]);

  /** Column visibility & order — always in a centered modal (rail / drawer are too narrow). */
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [autoSort, setAutoSort] = useState(true);
  const { openHistorical } = useHistoricalModal();

  const [consolidatedView, setConsolidatedView] = useState(() => {
    if (simpleLayout) return true;
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem('mmec_datatable_consolidated') === '1';
  });

  useEffect(() => {
    if (simpleLayout) return;
    try {
      localStorage.setItem('mmec_datatable_consolidated', consolidatedView ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [consolidatedView, simpleLayout]);

  const [consolidatedGroupBy, setConsolidatedGroupBy] = useState<ConsolidatedGroupBy>(() => {
    if (typeof localStorage === 'undefined') return 'strike_expiry';
    const v = localStorage.getItem('mmec_consolidated_group_by');
    return v === 'strike' ? 'strike' : 'strike_expiry';
  });

  useEffect(() => {
    try {
      localStorage.setItem('mmec_consolidated_group_by', consolidatedGroupBy);
    } catch {
      /* ignore */
    }
  }, [consolidatedGroupBy]);

  const [simpleFilterOpen, setSimpleFilterOpen] = useState(false);

  useEffect(() => {
    if (!simpleLayout || !simpleFilterOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSimpleFilterOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [simpleLayout, simpleFilterOpen]);

  const [simpleTableDensity, setSimpleTableDensity] = useState<SimpleScannerTableDensity>(() => loadSimpleScannerTableDensity());

  const commitSimpleTableDensity = useCallback((d: SimpleScannerTableDensity) => {
    setSimpleTableDensity(d);
    saveSimpleScannerTableDensity(d);
  }, []);

  const simpleDensityMetrics = useMemo(
    () => (simpleLayout ? getSimpleTableDensityMetrics(simpleTableDensity) : null),
    [simpleLayout, simpleTableDensity],
  );

  useEffect(() => {
    if (!columnPickerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setColumnPickerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [columnPickerOpen]);

  useEffect(() => {
    if (!advancedFilterModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAdvancedFilterModalOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advancedFilterModalOpen]);

  const [] = useState(false);
  const [] = useState<{ opportunities: any[]; leaders: Record<string, any[]> } | null>(null);
  const [] = useState(false);

  // Previous data for flashing effect
  const [previousData, setPreviousData] = useState<ScannerData[]>([]);
  const [flashingCells, setFlashingCells] = useState<Set<string>>(new Set());
  
  // Interactive features state
  const [alertNotifications, setAlertNotifications] = useState<Array<{
    id: string;
    type: 'premium' | 'movement' | 'new_entry' | 'unusual';
    message: string;
    timestamp: Date;
    symbol: string;
    value: number;
    oldValue?: number;
    oldPosition?: number;
    newPosition?: number;
  }>>([]);
  /** Previous top-10 symbol → row index; kept in a ref so the data-change effect does not re-run when we update it (new Map() each time would loop if this were state in the effect deps). */
  const top10PositionsRef = useRef<Map<string, number>>(new Map());
  const [previousPositions, setPreviousPositions] = useState<Map<string, number>>(new Map());
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  
  // Column resizing state
  const [isResizing, setIsResizing] = useState(false);
  const [resizingColumn, setResizingColumn] = useState<string | null>(null);

  /** Main grid scroll + synced horizontal strips (top/bottom). */
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const hScrollTopRef = useRef<HTMLDivElement>(null);
  const hScrollTopInnerRef = useRef<HTMLDivElement>(null);
  const hScrollBottomRef = useRef<HTMLDivElement>(null);
  const hScrollBottomInnerRef = useRef<HTMLDivElement>(null);
  /** Column drag resize (pointer API so simple / mobile works; avoids stale mousemove closure). */
  const resizeDragRef = useRef<{ key: string; startX: number; startW: number; pointerId: number } | null>(null);
  const resizeCaptureElRef = useRef<HTMLElement | null>(null);

  // RTL / default layout (stable-specific defaults applied when entering Stable; see effect below)
  const defaultColumns = buildDefaultColumns(language, false, effectiveDataMode);

  // Alert configuration settings
  const [alertSettings, setAlertSettings] = useState({
    premiumChangeThreshold: 50, // Percentage change threshold for premium alerts
    positionChangeThreshold: 5, // Minimum position change to trigger alert
    enablePremiumAlerts: false,
    enablePositionAlerts: false,
    enableNewEntryAlerts: false,
    enableUnusualAlerts: false,
    maxAlertsHistory: 50, // Maximum number of alerts to keep
    alertSoundVolume: 0.5, // Volume for alert sounds (0-1)
    notificationSoundVolume: 0.3, // Volume for notification sounds (0-1)
    selectedAlertSound: 'bell-notification-337658', // Selected alert sound
    selectedNotificationSound: 'bell-sound-370341', // Selected notification sound
    // Delta Premium alerts
    deltaPremiumAboveThreshold: 1000000, // Alert when Delta Premium is above this value
    deltaPremiumBelowThreshold: -1000000, // Alert when Delta Premium is below this value
    enableDeltaPremiumAlerts: true,
    // Delta Accumulated Premium alerts
    deltaAccumulatedPremiumAboveThreshold: 10000, // Alert when Delta Accumulated Premium is above this value
    deltaAccumulatedPremiumBelowThreshold: -10000, // Alert when Delta Accumulated Premium is below this value
    enableDeltaAccumulatedPremiumAlerts: false,
    // Delta (Ask - Bid) alerts
    deltaAskBidAboveThreshold: 5000, // Alert when Delta (Ask - Bid) is above this value
    deltaAskBidBelowThreshold: -5000, // Alert when Delta (Ask - Bid) is below this value
    enableDeltaAskBidAlerts: true,
    // Delta Accumulated alerts
    deltaAccumulatedAboveThreshold: 1000, // Alert when Delta Accumulated is above this value
    deltaAccumulatedBelowThreshold: -1000, // Alert when Delta Accumulated is below this value
    enableDeltaAccumulatedAlerts: false,
  });
  
  // Data table settings for export/import
  const [, setTableSettings] = useState({
    columns: defaultColumns,
    sortConfig: null as { key: string; direction: 'asc' | 'desc' } | null,
    itemsPerPage: 50,
    currentPage: 1,
    filters: {} as { [key: string]: string },
    filterGroups: [] as FilterGroup[],
    useAdvancedFiltering: false,
    autoSort: true,
    soundEnabled: false,
    alertsEnabled: false,
  });
  
  // Audio cache for MP3 files
  const audioCacheRef = useRef<{ [key: string]: HTMLAudioElement }>({});

  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>(defaultColumns);

  // Fetch available underlyings for current data source (RTL = CBOE list, Stable = CSV) so list is never stuck on wrong source
  useEffect(() => {
    const fetchUnderlyings = async () => {
      try {
        const underlyings = await dataService.fetchAvailableUnderlyings(dataSource);
        setApiUnderlyings(underlyings || []);
      } catch (err) {
        console.warn('Failed to fetch underlyings from API:', err);
      }
    };
    fetchUnderlyings();
    const interval = setInterval(fetchUnderlyings, 30000);
    return () => clearInterval(interval);
  }, [dataSource]);

  // When data source switches to Legacy RTL: reset filters and clear selected underlyings.
  useEffect(() => {
    if (dataSource === 'rtl_legacy') {
      setFilters(getAllDefaultsFilters());
      setSelectedUnderlyings([]);
      localStorage.setItem('mmec_expiry_range', '');
      localStorage.setItem('mmec_selected_underlyings', JSON.stringify([]));
      localStorage.setItem('mmec_selected_underlying', '');
    }
  }, [dataSource]);

  // When data source switches to Legacy RTL (via event), refetch underlyings so list is not stuck on CSV
  useEffect(() => {
    const handler = async (e: Event) => {
      const ev = e as CustomEvent<{ source?: string }>;
      if (ev?.detail?.source === 'rtl_legacy') {
        try {
          const list = await dataService.fetchAvailableUnderlyings('rtl_legacy');
          setApiUnderlyings(list || []);
        } catch (err) {
          console.warn('[DataTable] Refetch underlyings on RTL switch failed', err);
        }
      }
    };
    window.addEventListener('dataSourceChanged', handler);
    return () => window.removeEventListener('dataSourceChanged', handler);
  }, []);

  // Stable (CBOE): when data loads for one underlying, sync selectedUnderlyings so the table shows it
  useEffect(() => {
    const dataSource = typeof window !== 'undefined' ? (localStorage.getItem('mmec_data_source') || 'rtl_legacy') : 'rtl_legacy';
    if (dataSource !== 'stable' || !deferredData?.length) return;
    const fromData = Array.from(new Set(deferredData.map(r => r.Underlying).filter(Boolean))).map(String);
    if (fromData.length === 0) return;
    setSelectedUnderlyings(prev => {
      const same = prev.length === fromData.length && fromData.every(u => prev.includes(u));
      if (same) return prev;
      return fromData;
    });
  }, [deferredData]);

  // When using fetch (stable): add all extra fields from the endpoint to the table as optional columns
  useEffect(() => {
    if (dataSource !== 'stable' || !deferredData?.length) return;
    const schemaProbeSlice = deferredData.slice(0, 100);
    const dataKeys = new Set<string>();
    schemaProbeSlice.forEach((row: ScannerData) => {
      if (row && typeof row === 'object') Object.keys(row).forEach((k) => dataKeys.add(k));
    });
    setColumnConfig((prev) => {
      const existingKeys = new Set(prev.map((c) => c.key));
      const toAdd = Array.from(dataKeys).filter((k) => !existingKeys.has(k) && typeof k === 'string' && k.length > 0 && !k.startsWith('_'));
      if (toAdd.length === 0) return prev;
      const maxOrder = Math.max(0, ...prev.map((c) => c.order ?? 0));
      const newCols = toAdd.map((key, i) => ({
        key,
        label: key,
        visible: false,
        order: maxOrder + 1 + i,
        width: COLUMN_WIDTH_CATALOG_DEFAULT,
      }));
      return [...prev, ...newCols];
    });
  }, [dataSource, deferredData]);

  // Entering Stable: apply preset (Volume, OI, Overall Premium, core identity) while keeping extra API-only columns
  useEffect(() => {
    const prev = prevDataSourceForColumnsRef.current;
    prevDataSourceForColumnsRef.current = dataSource;
    if (dataSource !== 'stable' || prev === 'stable') return;
    setColumnConfig((prevCols) => {
      const preset = buildDefaultColumns(language, true);
      const presetKeys = new Set(preset.map((c) => c.key));
      const extras = prevCols.filter((c) => !presetKeys.has(c.key));
      return [...preset, ...extras];
    });
  }, [dataSource, language]);

  // RTL Legacy: re-apply column preset when switching 5min ↔ accumulated
  useEffect(() => {
    if (dataSource !== 'rtl_legacy') return;
    const prev = prevDataModeForColumnsRef.current;
    prevDataModeForColumnsRef.current = effectiveDataMode;
    if (prev === effectiveDataMode) return;
    setColumnConfig(buildDefaultColumns(language, false, effectiveDataMode));
  }, [effectiveDataMode, dataSource, language]);

  // Handle underlying selection toggle (multi-select)
  const handleUnderlyingToggle = useCallback((underlying: string) => {
    setSelectedUnderlyings(prev => {
      const isSelected = prev.includes(underlying);
      const newSelection = isSelected 
        ? prev.filter(u => u !== underlying)
        : [...prev, underlying];
      
      if (dataSource === 'stable') {
        setStableTabSelection(newSelection);
      } else {
        localStorage.setItem('mmec_selected_underlyings', JSON.stringify(newSelection));
        localStorage.setItem('mmec_selected_underlying', newSelection[0] || '');
      }
      
      // Trigger data refresh with first selected underlying
      window.dispatchEvent(new CustomEvent('underlyingChanged', { 
        detail: { underlying: newSelection[0] || '', underlyings: newSelection } 
      }));
      return newSelection;
    });
  }, [dataSource]);

  // Select/deselect all underlyings - defined as regular function to avoid dependency issues
  const handleSelectAllUnderlyings = (selectAll: boolean, allUnderlyings: string[]) => {
    const newSelection = selectAll ? [...allUnderlyings] : [];
    setSelectedUnderlyings(newSelection);
    if (dataSource === 'stable') {
      setStableTabSelection(newSelection);
    } else {
      localStorage.setItem('mmec_selected_underlyings', JSON.stringify(newSelection));
      localStorage.setItem('mmec_selected_underlying', newSelection[0] || '');
    }
    window.dispatchEvent(new CustomEvent('underlyingChanged', { 
      detail: { underlying: newSelection[0] || '', underlyings: newSelection } 
    }));
  };

  // Load settings from localStorage on component mount
  useEffect(() => {
    const savedAlertSettings = localStorage.getItem('dataTable_alertSettings');
    if (savedAlertSettings) {
      try {
        const parsed = JSON.parse(savedAlertSettings);
        setAlertSettings(prev => ({ ...prev, ...parsed }));
      } catch (error) {
        console.warn('Failed to load alert settings:', error);
      }
    }

    const savedTableSettings = localStorage.getItem('dataTable_settings');
    if (savedTableSettings) {
      try {
        const parsed = JSON.parse(savedTableSettings);
        setTableSettings(prev => ({ ...prev, ...parsed }));
        // Apply loaded settings
        if (parsed.columns) setColumnConfig(parsed.columns);
        if (parsed.sortConfig) setSortConfig(parsed.sortConfig);
        if (parsed.itemsPerPage) setItemsPerPage(parsed.itemsPerPage);
        if (parsed.currentPage) setCurrentPage(parsed.currentPage);
        if (parsed.filters) {
          const loadedFilters = { ...getAllDefaultsFilters(), ...parsed.filters };
          // Default expiry to "all" for both RTL and Stable (fetching has its own filtering)
          loadedFilters['Days Left'] = '';
          setFilters(loadedFilters);
        }
        if (parsed.filterGroups) setFilterGroups(parsed.filterGroups);
        if (parsed.useAdvancedFiltering !== undefined) setUseAdvancedFiltering(parsed.useAdvancedFiltering);
        if (parsed.autoSort !== undefined) setAutoSort(parsed.autoSort);
        if (parsed.soundEnabled !== undefined) setSoundEnabled(parsed.soundEnabled);
        if (parsed.alertsEnabled !== undefined) setAlertsEnabled(parsed.alertsEnabled);
      } catch (error) {
        console.warn('Failed to load table settings:', error);
      }
    }
    
    // Default expiry range to "all" for both RTL and Stable (fetching has its own filtering)
    const currentExpiry = localStorage.getItem('mmec_expiry_range');
    if (!currentExpiry) {
      localStorage.setItem('mmec_expiry_range', '');
    }
  }, []);

  // Stable (CBOE): data is for one underlying only — keep selection in sync so table shows rows
  useEffect(() => {
    const ds = typeof window !== 'undefined' ? (localStorage.getItem('mmec_data_source') || 'rtl_legacy') : 'rtl_legacy';
    if (ds !== 'stable' || !data?.length) return;
    const fromData = Array.from(new Set(data.map((item: ScannerData) => item.Underlying).filter(Boolean)));
    if (fromData.length === 0) return;
    setSelectedUnderlyings(prev => {
      const same = prev.length === fromData.length && fromData.every((u: string) => prev.includes(u));
      return same ? prev : fromData;
    });
  }, [data]);

  // Save settings to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('dataTable_alertSettings', JSON.stringify(alertSettings));
  }, [alertSettings]);

  useEffect(() => {
    const settingsToSave = {
      columns: columnConfig,
      sortConfig,
      itemsPerPage,
      currentPage,
      filters,
      filterGroups,
      useAdvancedFiltering,
      autoSort,
      soundEnabled,
      alertsEnabled,
    };
    localStorage.setItem('dataTable_settings', JSON.stringify(settingsToSave));
  }, [columnConfig, sortConfig, itemsPerPage, currentPage, filters, filterGroups, useAdvancedFiltering, autoSort, soundEnabled, alertsEnabled]);

  // Initialize audio cache for MP3 files
  useEffect(() => {
    // Preload the selected sounds
    const preloadSound = (filename: string) => {
      if (!filename || audioCacheRef.current[filename]) return;
      
      const audio = new Audio(`/${filename}`);
      audio.preload = 'auto';
      audioCacheRef.current[filename] = audio;
    };
    
    // Preload selected sounds
    preloadSound(alertSettings.selectedAlertSound);
    preloadSound(alertSettings.selectedNotificationSound);
  }, [alertSettings.selectedAlertSound, alertSettings.selectedNotificationSound]);

  // Play sound effect for sorting changes
  const playSortSound = useCallback(() => {
    if (soundEnabled && alertSettings.selectedNotificationSound !== 'none') {
      const soundOption = ALERT_SOUNDS.find(s => s.id === alertSettings.selectedNotificationSound);
      if (soundOption && soundOption.filename) {
        const audio = audioCacheRef.current[soundOption.filename] || new Audio(`/${soundOption.filename}`);
        audio.volume = alertSettings.notificationSoundVolume;
        audio.play().catch(console.error);
      }
    }
  }, [soundEnabled, alertSettings.selectedNotificationSound, alertSettings.notificationSoundVolume]);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && alertSettings.selectedNotificationSound !== 'none') {
      const soundOption = ALERT_SOUNDS.find(s => s.id === alertSettings.selectedNotificationSound);
      if (soundOption && soundOption.filename) {
        const audio = audioCacheRef.current[soundOption.filename] || new Audio(`/${soundOption.filename}`);
        audio.volume = alertSettings.notificationSoundVolume;
        audio.play().catch(console.error);
      }
    }
  }, [soundEnabled, alertSettings.selectedNotificationSound, alertSettings.notificationSoundVolume]);

  // Play alert sound
  const playAlertSound = useCallback(() => {
    if (soundEnabled && alertSettings.selectedAlertSound !== 'none') {
      const soundOption = ALERT_SOUNDS.find(s => s.id === alertSettings.selectedAlertSound);
      if (soundOption && soundOption.filename) {
        const audio = audioCacheRef.current[soundOption.filename] || new Audio(`/${soundOption.filename}`);
        audio.volume = alertSettings.alertSoundVolume;
        audio.play().catch(console.error);
      }
    }
  }, [soundEnabled, alertSettings.selectedAlertSound, alertSettings.alertSoundVolume]);

  // Add alert notification
  const addAlert = useCallback((type: 'premium' | 'movement' | 'new_entry' | 'unusual', message: string, symbol: string, value: number, oldValue?: number, oldPosition?: number, newPosition?: number) => {
    if (!alertsEnabled) return;
    
    const alert = {
      id: `${Date.now()}-${randomClientIdSuffix()}`,
      type,
      message,
      timestamp: new Date(),
      symbol,
      value,
      oldValue,
      oldPosition,
      newPosition
    };
    
    setAlertNotifications(prev => [alert, ...prev.slice(0, alertSettings.maxAlertsHistory - 1)]); // Keep only last N alerts
    
    // Play appropriate sound
    if (type === 'unusual' || type === 'new_entry') {
      playAlertSound();
    } else {
      playNotificationSound();
    }
  }, [alertsEnabled, playAlertSound, playNotificationSound]);

  // Export settings to JSON
  const exportSettings = useCallback(() => {
    const settings = {
      alertSettings,
      tableSettings: {
        columns: columnConfig,
        sortConfig,
        itemsPerPage,
        currentPage,
        filters,
        filterGroups,
        useAdvancedFiltering,
        autoSort,
        soundEnabled,
        alertsEnabled,
      },
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(settings, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dataTable_settings_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [alertSettings, columnConfig, sortConfig, itemsPerPage, currentPage, filters, filterGroups, useAdvancedFiltering, autoSort, soundEnabled, alertsEnabled]);


  // Import settings from JSON
  const importSettings = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const settings = JSON.parse(e.target?.result as string);
        
        if (settings.alertSettings) {
          setAlertSettings(prev => ({ ...prev, ...settings.alertSettings }));
        }
        
        if (settings.tableSettings) {
          const tableSettings = settings.tableSettings;
          if (tableSettings.columns) setColumnConfig(tableSettings.columns);
          if (tableSettings.sortConfig) setSortConfig(tableSettings.sortConfig);
          if (tableSettings.itemsPerPage) setItemsPerPage(tableSettings.itemsPerPage);
          if (tableSettings.currentPage) setCurrentPage(tableSettings.currentPage);
          if (tableSettings.filters) setFilters(tableSettings.filters);
          if (tableSettings.filterGroups) setFilterGroups(tableSettings.filterGroups);
          if (tableSettings.useAdvancedFiltering !== undefined) setUseAdvancedFiltering(tableSettings.useAdvancedFiltering);
          if (tableSettings.autoSort !== undefined) setAutoSort(tableSettings.autoSort);
          if (tableSettings.soundEnabled !== undefined) setSoundEnabled(tableSettings.soundEnabled);
          if (tableSettings.alertsEnabled !== undefined) setAlertsEnabled(tableSettings.alertsEnabled);
        }
        
        alert('Settings imported successfully!');
      } catch (error) {
        alert('Failed to import settings. Please check the file format.');
        console.error('Import error:', error);
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  }, []);

  // Clear all alerts
  const clearAlerts = useCallback(() => {
    setAlertNotifications([]);
  }, []);

  // Enhanced change detection with position tracking and alerts
  useEffect(() => {
    if (previousData.length > 0 && data.length > 0) {
      const newFlashingCells = new Set<string>();
      const currentPositions = new Map<string, number>();
      
      // Create current position map
      data.forEach((item, index) => {
        const symbol = item.Symbol || `${item.Underlying}-${item.Strike}-${item['Option Type']}`;
        currentPositions.set(symbol, index);
      });
      
      // Check for position changes in top 10
      const top10Symbols = new Set(data.slice(0, 10).map(item => item.Symbol || `${item.Underlying}-${item.Strike}-${item['Option Type']}`));
      
      data.forEach((item, rowIndex) => {
        const symbol = item.Symbol || `${item.Underlying}-${item.Strike}-${item['Option Type']}`;
        const prevItem = previousData.find(p => (p.Symbol || `${p.Underlying}-${p.Strike}-${p['Option Type']}`) === symbol);
        const oldPosition = previousPositions.get(symbol);
        const newPosition = currentPositions.get(symbol);
        
        
        if (prevItem) {
          // Check for value changes
          Object.keys(item).forEach(fieldName => {
            if (item[fieldName as keyof ScannerData] !== prevItem[fieldName as keyof ScannerData]) {
              newFlashingCells.add(`${rowIndex}-${fieldName}`);
              
              // Check for significant changes in premium fields
              if (fieldName.includes('Premium') && typeof item[fieldName as keyof ScannerData] === 'number' && typeof prevItem[fieldName as keyof ScannerData] === 'number') {
                const currentValue = item[fieldName as keyof ScannerData] as number;
                const previousValue = prevItem[fieldName as keyof ScannerData] as number;
                const changePercent = Math.abs((currentValue - previousValue) / (previousValue || 1)) * 100;
                
                if (changePercent > alertSettings.premiumChangeThreshold && alertSettings.enablePremiumAlerts) {
                  addAlert('premium', `Unusual ${fieldName} change: ${previousValue.toFixed(2)} → ${currentValue.toFixed(2)} (${changePercent.toFixed(1)}%)`, symbol, currentValue, previousValue);
                }
              }
            }
          });
        }
        
        // Check for position changes
        if (oldPosition !== undefined && newPosition !== undefined && oldPosition !== newPosition) {
          const positionChange = oldPosition - newPosition; // Positive means moved up
          
          // Flash the entire row for position changes
          newFlashingCells.add(`${rowIndex}-position`);
          
          // Alert for significant position changes
          if (Math.abs(positionChange) >= alertSettings.positionChangeThreshold && alertSettings.enablePositionAlerts) {
            const direction = positionChange > 0 ? 'moved up' : 'moved down';
            addAlert('movement', `${symbol} ${direction} ${Math.abs(positionChange)} positions (${oldPosition + 1} → ${newPosition + 1})`, symbol, newPosition, undefined, oldPosition, newPosition);
          }
          
          // Special alert for entering top 10
          if (oldPosition >= 10 && newPosition < 10 && alertSettings.enableNewEntryAlerts) {
            addAlert('new_entry', `${symbol} entered top 10 at position ${newPosition + 1}`, symbol, newPosition, undefined, oldPosition, newPosition);
          }
        }
        
        // Check for new entries in top 10
        if (top10Symbols.has(symbol) && !top10PositionsRef.current.has(symbol) && alertSettings.enableNewEntryAlerts) {
          addAlert('new_entry', `${symbol} appeared in top 10 at position ${newPosition! + 1}`, symbol, newPosition!);
        }
      });
      
      setFlashingCells(newFlashingCells);
      setPreviousPositions(currentPositions);
      top10PositionsRef.current = new Map(data.slice(0, 10).map((item, index) => [
        item.Symbol || `${item.Underlying}-${item.Strike}-${item['Option Type']}`, 
        index
      ]));
      
      // Clear flashing after animation
      setTimeout(() => setFlashingCells(new Set()), 3000);
    } else if (data.length > 0) {
      // First load - initialize positions
      const currentPositions = new Map<string, number>();
      data.forEach((item, index) => {
        const symbol = item.Symbol || `${item.Underlying}-${item.Strike}-${item['Option Type']}`;
        currentPositions.set(symbol, index);
      });
      setPreviousPositions(currentPositions);
      top10PositionsRef.current = new Map(data.slice(0, 10).map((item, index) => [
        item.Symbol || `${item.Underlying}-${item.Strike}-${item['Option Type']}`, 
        index
      ]));
    }
    
    setPreviousData(data);
  }, [data, sortConfig, addAlert]);

  // Separate effect for threshold monitoring (runs on every data update)
  useEffect(() => {
    if (data.length > 0) {
      data.forEach(item => {
        const symbol = item.Symbol || `${item.Underlying}-${item.Strike}-${item['Option Type']}`;
        
        // Check current values against thresholds (regardless of data changes)
        Object.keys(item).forEach(fieldName => {
          // Check Delta Premium thresholds
          if (fieldName === 'Delta Premium' && typeof item[fieldName as keyof ScannerData] === 'number' && alertSettings.enableDeltaPremiumAlerts) {
            const currentValue = item[fieldName as keyof ScannerData] as number;
            if (currentValue > alertSettings.deltaPremiumAboveThreshold) {
              addAlert('premium', `Delta Premium above threshold: ${currentValue.toFixed(2)} > ${alertSettings.deltaPremiumAboveThreshold}`, symbol, currentValue);
            } else if (currentValue < alertSettings.deltaPremiumBelowThreshold) {
              addAlert('premium', `Delta Premium below threshold: ${currentValue.toFixed(2)} < ${alertSettings.deltaPremiumBelowThreshold}`, symbol, currentValue);
            }
          }

          // Check Delta Accumulated Premium thresholds
          if (fieldName === 'Delta Accumulated Premium' && typeof item[fieldName as keyof ScannerData] === 'number' && alertSettings.enableDeltaAccumulatedPremiumAlerts) {
            const currentValue = item[fieldName as keyof ScannerData] as number;
            if (currentValue > alertSettings.deltaAccumulatedPremiumAboveThreshold) {
              addAlert('premium', `Delta Accumulated Premium above threshold: ${currentValue.toFixed(2)} > ${alertSettings.deltaAccumulatedPremiumAboveThreshold}`, symbol, currentValue);
            } else if (currentValue < alertSettings.deltaAccumulatedPremiumBelowThreshold) {
              addAlert('premium', `Delta Accumulated Premium below threshold: ${currentValue.toFixed(2)} < ${alertSettings.deltaAccumulatedPremiumBelowThreshold}`, symbol, currentValue);
            }
          }

          // Check Delta (Ask - Bid) thresholds
          if (fieldName === 'Delta ( ask - bid )' && typeof item[fieldName as keyof ScannerData] === 'number' && alertSettings.enableDeltaAskBidAlerts) {
            const currentValue = item[fieldName as keyof ScannerData] as number;
            if (currentValue > alertSettings.deltaAskBidAboveThreshold) {
              addAlert('premium', `Delta (Ask-Bid) above threshold: ${currentValue.toFixed(2)} > ${alertSettings.deltaAskBidAboveThreshold}`, symbol, currentValue);
            } else if (currentValue < alertSettings.deltaAskBidBelowThreshold) {
              addAlert('premium', `Delta (Ask-Bid) below threshold: ${currentValue.toFixed(2)} < ${alertSettings.deltaAskBidBelowThreshold}`, symbol, currentValue);
            }
          }

          // Check Delta Accumulated thresholds
          if (fieldName === 'Delta accumelated' && typeof item[fieldName as keyof ScannerData] === 'number' && alertSettings.enableDeltaAccumulatedAlerts) {
            const currentValue = item[fieldName as keyof ScannerData] as number;
            if (currentValue > alertSettings.deltaAccumulatedAboveThreshold) {
              addAlert('premium', `Delta Accumulated above threshold: ${currentValue.toFixed(2)} > ${alertSettings.deltaAccumulatedAboveThreshold}`, symbol, currentValue);
            } else if (currentValue < alertSettings.deltaAccumulatedBelowThreshold) {
              addAlert('premium', `Delta Accumulated below threshold: ${currentValue.toFixed(2)} < ${alertSettings.deltaAccumulatedBelowThreshold}`, symbol, currentValue);
            }
          }
        });
      });
    }
  }, [data, alertSettings, addAlert]);

  const effectiveColumnConfig = useMemo(() => columnConfig, [columnConfig]);

  // When Stable: hide bid size, ask size, delta (ask-bid), delta accum, delta premium, delta acc premium
  const columnsHiddenForStableOrNewRtl = useMemo(() => new Set([
    'Bid Volume', 'Ask Volume', 'Delta ( ask - bid )', 'Delta accumelated',
    'Delta Premium', 'Delta Accumulated Premium',
  ]), []);

  // Get visible columns in order (exclude stable/new-RTL-hidden columns when source is stable or rtl)
  const visibleColumns = useMemo(() => {
    let cols = effectiveColumnConfig.filter(col => col.visible).sort((a, b) => a.order - b.order);
    if (dataSource === 'stable') {
      cols = cols.filter(col => !columnsHiddenForStableOrNewRtl.has(col.key));
    }
    return cols;
  }, [effectiveColumnConfig, dataSource, columnsHiddenForStableOrNewRtl]);

  // Get frozen and scrollable columns (at most Underlying + Side + Strike sticky)
  const frozenColumns = useMemo(() => {
    return visibleColumns.filter((col) => columnIsStickyInLayout(col));
  }, [visibleColumns]);

  const scrollableColumns = useMemo(() => {
    return visibleColumns.filter((col) => !columnIsStickyInLayout(col));
  }, [visibleColumns]);

  // Get available filter options from data

  const availableExpiries = useMemo(() => {
    const expiries = Array.from(new Set(data.map(item => getExpiryFromRecord(item)).filter(Boolean)));
    return expiries.sort();
  }, [data]);



  const hasActiveAdvancedFilters = useMemo(
    () => filterGroups.some((g) => g.conditions.length > 0),
    [filterGroups]
  );

  // Filter data - minimal client-side filtering, backend does heavy work
  const filteredData = useMemo(() => {
    let filtered = deferredData || [];
    if (filtered.length === 0) return [];

    if (selectedUnderlyings.length > 0) {
      filtered = filtered.filter((item) =>
        legacyUnderlyingSelectionMatches(item.Underlying, selectedUnderlyings)
      );
    }

    const groupsWithConditions = filterGroups.filter((g) => g.conditions.length > 0);

    const passesAdvancedFilters = (item: ScannerData): boolean => {
      if (groupsWithConditions.length === 0) return true;
      return groupsWithConditions.every((group) => {
        let groupResult = true;
        for (let i = 0; i < group.conditions.length; i++) {
          const condition = group.conditions[i];
          const itemValue = getFilterFieldValue(item, String(condition.field));
          const conditionResult = evaluateFilterCondition(itemValue, condition);
          if (i === 0) {
            groupResult = conditionResult;
          } else if ((group.logicalOperator ?? 'AND') === 'AND') {
            groupResult = groupResult && conditionResult;
          } else {
            groupResult = groupResult || conditionResult;
          }
        }
        return groupResult;
      });
    };

    filtered = filtered.filter((item) => {
      if (!passesAdvancedFilters(item)) return false;
        // Underlying text filter (applies when set; RTL multi-select is separate)
        if (filters.Underlying) {
          const underlyingValue = String(item.Underlying || '').toLowerCase();
          if (!underlyingValue.includes(filters.Underlying.toLowerCase())) {
            return false;
          }
        }

        // Strike filter
        if (filters.Strike) {
          const strikeValue = String(item.Strike || '').toLowerCase();
          if (!strikeValue.includes(filters.Strike.toLowerCase())) {
            return false;
          }
        }

        // Option Type (Side) filter - proper Call/Put matching
        if (filters['Option Type']) {
          const optionType = String(item['Option Type'] || '').toUpperCase();
          const filterValue = String(filters['Option Type']).toUpperCase();
          
          // Handle both 'C'/'P' and 'CALL'/'PUT' formats
          const normalizedOptionType = optionType === 'C' ? 'CALL' : optionType === 'P' ? 'PUT' : optionType;
          const normalizedFilterValue = filterValue === 'C' ? 'CALL' : filterValue === 'P' ? 'PUT' : filterValue;
          
          if (normalizedOptionType !== normalizedFilterValue) {
            return false;
          }
        }

        // Moneyness filter - proper ITM/ATM/OTM calculation
        if (filters.Moneyness) {
          const underlyingPrice = Number(item['Underlying Price'] || 0);
          const strike = Number(item.Strike || 0);
          const optionType = String(item['Option Type'] || '').toUpperCase();
          
          if (underlyingPrice > 0 && strike > 0) {
            const moneynessCategory = calculateMoneynessCategoryFromPrice(strike, underlyingPrice, optionType);
            if (moneynessCategory !== filters.Moneyness) {
              return false;
            }
          }
        }

        // Category filter — skip in RTL/Legacy so all underlyings (including indices) show
        if (dataSource !== 'rtl_legacy') {
          const underlying = String(item.Underlying || '');
          if (!matchesCategoryFilter(underlying, selectedCategory)) {
            return false;
          }
        }

        // Expiry Date filter
        if (filters['Expiry Date']) {
          const expiryValue = formatDate(item['Expiry Date'] || '');
          if (expiryValue !== filters['Expiry Date']) {
            return false;
          }
        }

        // Days Left / Expiry Range filter
        if (filters['Days Left']) {
          const expiryDate = new Date(getExpiryFromRecord(item) || item['Expiry Date'] || '');
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Normalize to start of day
          const daysLeft = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          const filterRange = filters['Days Left'];
          let matches = false;
          
          // Helper: Get days until next Friday
          const getDaysToFriday = () => {
            const dayOfWeek = today.getDay(); // 0=Sunday, 5=Friday
            if (dayOfWeek <= 5) {
              return 5 - dayOfWeek; // Days until this Friday
            }
            return 6; // Saturday -> next Friday is 6 days
          };
          
          // Helper: Get days until end of month
          const getDaysToEndOfMonth = () => {
            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            return Math.ceil((endOfMonth.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          };
          
          // Helper: Get days until end of quarter
          const getDaysToEndOfQuarter = () => {
            const quarter = Math.floor(today.getMonth() / 3);
            const endOfQuarter = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
            return Math.ceil((endOfQuarter.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          };
          
          // Helper: Check if expiry is a monthly option (3rd Friday of month)
          const isMonthlyExpiry = () => {
            const expiryDay = expiryDate.getDay();
            const expiryDateNum = expiryDate.getDate();
            // Monthly options expire on 3rd Friday (between 15th-21st and is Friday)
            return expiryDay === 5 && expiryDateNum >= 15 && expiryDateNum <= 21;
          };
          
          // Helper: Check if expiry is a weekly option (Friday but not 3rd Friday)
          const isWeeklyExpiry = () => {
            const expiryDay = expiryDate.getDay();
            const expiryDateNum = expiryDate.getDate();
            // Weekly options expire on Fridays but NOT 3rd Friday
            return expiryDay === 5 && !(expiryDateNum >= 15 && expiryDateNum <= 21);
          };
          
          switch (filterRange) {
            case 'this-week':
              // From today to closest Friday (including today if Friday)
              matches = daysLeft >= 0 && daysLeft <= getDaysToFriday();
              break;
            case 'weekly':
              // Weekly contracts (Fridays excluding 3rd Friday)
              matches = isWeeklyExpiry() && daysLeft >= 0;
              break;
            case 'monthly':
              // Monthly contracts (3rd Friday of each month)
              matches = isMonthlyExpiry() && daysLeft >= 0;
              break;
            case 'this-month':
              // All expiries within this calendar month
              matches = daysLeft >= 0 && daysLeft <= getDaysToEndOfMonth();
              break;
            case '3-months':
              // All expiries within next 3 months (90 days)
              matches = daysLeft >= 0 && daysLeft <= 90;
              break;
            case 'quarter':
              // All expiries within current quarter
              matches = daysLeft >= 0 && daysLeft <= getDaysToEndOfQuarter();
              break;
            case '6-months':
              // All expiries within next 6 months (182 days)
              matches = daysLeft >= 0 && daysLeft <= 182;
              break;
            case 'annual':
              // All expiries within next year (365 days)
              matches = daysLeft >= 0 && daysLeft <= 365;
              break;
            case 'specific-date': {
              const want = stableSpecificExpiryDate.trim();
              if (!want) {
                matches = true;
                break;
              }
              const itemExp = formatDate(item['Expiry Date'] || '') || String(item['Expiry Date'] || '').trim();
              const wantNorm = want.replace(/\//g, '-').slice(0, 10);
              const itemNorm = itemExp.replace(/\//g, '-').slice(0, 10);
              matches =
                itemNorm === wantNorm ||
                itemNorm.replace(/-/g, '') === wantNorm.replace(/-/g, '');
              break;
            }
            case '1':
              matches = daysLeft >= 0 && daysLeft <= 1;
              break;
            case '2-3':
              matches = daysLeft >= 2 && daysLeft <= 3;
              break;
            case '4-7':
              matches = daysLeft >= 4 && daysLeft <= 7;
              break;
            case '8-14':
              matches = daysLeft >= 8 && daysLeft <= 14;
              break;
            case '15-30':
              matches = daysLeft >= 15 && daysLeft <= 30;
              break;
            case '31-90':
              matches = daysLeft >= 31 && daysLeft <= 90;
              break;
            case '90+':
              matches = daysLeft > 90;
              break;
            default:
              matches = true; // Unknown filter, allow all
          }
          
          if (!matches) {
            return false;
          }
        }

        return true;
    });

    return filtered;
  }, [deferredData, filters, filterGroups, selectedCategory, selectedUnderlyings, stableSpecificExpiryDate, dataSource]);

  /** Default widths from current page data + header text (columns still at catalog preset only). */
  const contentFitWidths = useMemo(
    () => estimateScannerColumnContentWidths(visibleColumns, filteredData),
    [visibleColumns, filteredData],
  );

  const getColumnDisplayWidth = useCallback(
    (col: ColumnConfig) => {
      const presetW = SCANNER_COLUMN_PRESET_WIDTH[col.key] ?? COLUMN_WIDTH_CATALOG_DEFAULT;
      const stored = Number(col.width);
      const hasStored = Number.isFinite(stored) && stored > 0;
      const atPreset = !hasStored || stored === presetW;
      const fitPx = atPreset ? contentFitWidths[col.key] : undefined;

      if (fitPx != null) {
        if (!simpleDensityMetrics) return Math.max(50, fitPx);
        return Math.max(simpleDensityMetrics.minColFloor, Math.round(fitPx));
      }

      if (!simpleDensityMetrics) {
        return Math.max(50, hasStored ? stored : presetW);
      }
      if (!hasStored) {
        return Math.max(simpleDensityMetrics.minColFloor, simpleDensityMetrics.defaultColWidth);
      }
      if (stored === COLUMN_WIDTH_CATALOG_DEFAULT) {
        return Math.max(simpleDensityMetrics.minColFloor, simpleDensityMetrics.defaultColWidth);
      }
      return Math.max(simpleDensityMetrics.minColFloor, Math.round(stored));
    },
    [simpleDensityMetrics, contentFitWidths],
  );

  /** Horizontal offset for each frozen column so `position: sticky; left` stacks correctly. */
  const frozenStickyLefts = useMemo(() => {
    const lefts: number[] = [];
    let acc = 0;
    for (const col of frozenColumns) {
      lefts.push(acc);
      acc += getColumnDisplayWidth(col);
    }
    return lefts;
  }, [frozenColumns, getColumnDisplayWidth]);

  /** Spot for consolidated strike ladder (% from spot, ATM highlight). */
  const consolidatedSpot = useMemo(() => {
    const prices = filteredData
      .map((r) => Number(r['Underlying Price' as keyof ScannerData] ?? 0))
      .filter((n) => Number.isFinite(n) && n > 0);
    if (prices.length === 0) return 0;
    return prices.reduce((a, b) => a + b, 0) / prices.length;
  }, [filteredData]);

  // Clear all filters
  const clearAllFilters = () => {
    setFilters(getAllDefaultsFilters());
    setFilterGroups([]);
    setUseAdvancedFiltering(false);
    setStableSpecificExpiryDate('');
    try {
      localStorage.removeItem('mmec_stable_specific_expiry');
    } catch {
      /* ignore */
    }
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof ScannerData];
      const bValue = b[sortConfig.key as keyof ScannerData];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Export data to CSV
  const exportToCSV = useCallback(() => {
    if (!filteredData || filteredData.length === 0) {
      alert(language === 'ar' ? 'لا توجد بيانات للتصدير' : 'No data to export');
      return;
    }

    // Get visible columns
    const visibleColumns = columnConfig.filter(col => col.visible);
    
    // Create CSV header
    const headers = visibleColumns.map(col => col.label).join(',');
    
    // Create CSV rows
    const rows = filteredData.map(row => {
      return visibleColumns.map(col => {
        const value = row[col.key as keyof ScannerData];
        // Handle different data types and escape commas
        if (value === null || value === undefined) return '';
        if (typeof value === 'string' && value.includes(',')) {
          return `"${value.replace(/"/g, '""')}"`; // Escape quotes in CSV
        }
        return String(value);
      }).join(',');
    });
    
    // Combine headers and rows
    const csvContent = [headers, ...rows].join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `scanner_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredData, columnConfig, language]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredData.length / itemsPerPage)),
    [filteredData.length, itemsPerPage],
  );

  /** Page numbers + ellipses for a single-line pager (compact rail / toolbar). */
  const pageListWithGaps = useMemo(() => {
    const t = totalPages;
    const c = currentPage;
    if (t <= 1) return [1] as (number | '…')[];
    if (t <= 11) return Array.from({ length: t }, (_, i) => i + 1) as (number | '…')[];
    const s = new Set([1, t]);
    for (let d = -2; d <= 2; d++) {
      const p = c + d;
      if (p >= 1 && p <= t) s.add(p);
    }
    const arr = Array.from(s).sort((a, b) => a - b);
    const out: (number | '…')[] = [];
    for (let i = 0; i < arr.length; i++) {
      if (i > 0 && arr[i] - arr[i - 1] > 1) out.push('…');
      out.push(arr[i]);
    }
    return out;
  }, [totalPages, currentPage]);

  // Clamp currentPage to valid range when filtered data length or itemsPerPage changes (fixes blank table when restored page is out of range)
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
    setCurrentPage(prev => {
      if (prev > totalPages) return totalPages;
      if (prev < 1) return 1;
      return prev;
    });
  }, [filteredData.length, itemsPerPage]);

  // Handle sorting with sound effect
  const handleSort = (key: string) => {
    const direction = sortConfig?.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    playSortSound();
  };

  // Get cell value with formatting
  const getCellValue = (item: ScannerData, key: string) => {
    // Special case for Days left - calculate from Expiry Date
    if (key === 'Days left') {
      const formatDaysLeftLabel = (daysLeft: number) => {
        if (daysLeft < 0) return 'Expired';
        if (daysLeft === 0) return 'Today';
        if (daysLeft === 1) return '1 day';
        return `${daysLeft} days`;
      };
      const expiryStr = getExpiryFromRecord(item) || item['Expiry Date'];
      if (expiryStr) {
        try {
          const expiry = new Date(expiryStr);
          const today = new Date();
          const expMs = expiry.getTime();
          const todayMs = today.getTime();
          if (Number.isFinite(expMs) && Number.isFinite(todayMs)) {
            const daysLeft = Math.ceil((expMs - todayMs) / (1000 * 60 * 60 * 24));
            if (Number.isFinite(daysLeft)) return formatDaysLeftLabel(daysLeft);
          }
        } catch {
          /* use backend field */
        }
      }
      const raw = item['Days left'];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        return formatDaysLeftLabel(Math.round(raw));
      }
      const parsed = parseFloat(String(raw ?? '').replace(/,/g, ''));
      if (Number.isFinite(parsed)) return formatDaysLeftLabel(Math.round(parsed));
      return '-';
    }
    
    const value = item[key as keyof ScannerData];
    
    if (value === null || value === undefined) return '-';
    
    // Format numbers
    if (typeof value === 'number') {
      // Premium fields - show in USD format with comma separators
      if (key.includes('Premium')) {
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      // Technical indicators - show as percentages with buy/sell indication
      if (key.includes('Haltz') || key.includes('Band') || key.includes('Mal') || key.includes('Zahn')) {
        const percentage = value.toFixed(1);
        const sign = value > 0 ? '+' : '';
        return `${sign}${percentage}%`;
      }
      if (key.includes('Delta') || key.includes('Gamma') || key.includes('Theta') || key.includes('Vega')) {
        return value.toFixed(4);
      }
      if (key.includes('Volume') || key.includes('Count')) {
        return value.toLocaleString();
      }
      if (key.includes('Price') || key.includes('Last') || key.includes('Strike')) {
        return value.toFixed(2);
      }
    }
    
    return String(value);
  };

  /** Design System preview: per-column |max| on the visible page, so heat = strength of the signal in its column. */
  const dsOn = isDesignSystemOn();
  const dsColMax = useMemo(() => {
    if (!dsOn) return null;
    const m: Record<string, number> = {};
    for (const row of paginatedData) {
      for (const [k, v] of Object.entries(row)) {
        if (typeof v === 'number' && Number.isFinite(v)) {
          const a = Math.abs(v);
          if (a > (m[k] ?? 0)) m[k] = a;
        }
      }
    }
    return m;
  }, [dsOn, paginatedData]);

  /** Design System cell: 12 px mono numbers, 30 px rows (32 Arabic), up/down heat at heat-1..5 alpha,
   *  Bid red / Ask green, large premium + volume as gold chip. Members never reach this branch. */
  const getDsCellStyle = (item: ScannerData, key: string, value: any): React.CSSProperties => {
    const textCol = isDataTableTextColumn(key);
    const rowH = language === 'ar' ? 32 : 30;
    const base: React.CSSProperties = {
      boxSizing: 'border-box',
      padding: simpleDensityMetrics?.cellPadding ?? '0 8px',
      borderRight: '1px solid',
      borderBottom: '1px solid',
      transition: 'background-color 150ms',
      textAlign: textCol ? 'start' : 'end',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      ...(textCol ? {} : { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' as const }),
      ...(simpleDensityMetrics
        ? { fontSize: simpleDensityMetrics.bodyFontPx, lineHeight: 1.28 }
        : { fontSize: 12, lineHeight: `${rowH - 1}px`, height: rowH }),
    };
    const raw = item[key as keyof ScannerData] as unknown;
    const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : NaN;
    const colMax = dsColMax?.[key] ?? 0;
    let tone: React.CSSProperties = {};
    const isHeat = key.includes('Delta') || key.includes('Haltz') || key.includes('Band') || key.includes('Mal') || key.includes('Zahn');
    const isBid = !isHeat && /\bbid\b/i.test(key);
    const isAsk = !isHeat && /\bask\b/i.test(key);
    if (isHeat && Number.isFinite(n) && n !== 0 && colMax > 0) {
      const lvl = Math.min(5, Math.max(1, Math.ceil((Math.abs(n) / colMax) * 5)));
      const alpha = [0, 0.18, 0.32, 0.5, 0.68, 0.85][lvl];
      const up = n > 0;
      tone = {
        backgroundColor: `rgba(${up ? '38,166,154' : '239,83,80'},${alpha})`,
        color: lvl >= 5 ? '#ffffff' : up ? 'var(--up-text)' : 'var(--down-text)',
      };
    } else if (isBid) {
      tone = { color: 'var(--down-text)' };
    } else if (isAsk) {
      tone = { color: 'var(--up-text)' };
    } else if ((key.includes('Premium') || key.includes('Volume')) && Number.isFinite(n) && n > 0 && colMax > 0 && n >= colMax * 0.8) {
      tone = { backgroundColor: 'var(--gold-chip)', color: 'var(--gold)', fontWeight: 600 };
    } else if (key.includes('Option Type')) {
      if (value === 'C' || value === 'CALL') tone = { color: 'var(--up-text)', fontWeight: 600 };
      else if (value === 'P' || value === 'PUT') tone = { color: 'var(--down-text)', fontWeight: 600 };
    }
    const rowIndex = paginatedData.indexOf(item);
    const flash: React.CSSProperties = flashingCells.has(`${rowIndex}-${key}`)
      ? { backgroundColor: 'var(--warning-wash)', color: 'var(--warning)', fontWeight: 600, animation: 'flash 0.5s ease-in-out' }
      : {};
    const posFlash: React.CSSProperties = flashingCells.has(`${rowIndex}-position`)
      ? { backgroundColor: 'var(--info-wash)', boxShadow: 'inset 2px 0 0 var(--info)' }
      : {};
    return { ...base, ...tone, ...flash, ...posFlash };
  };

  // Get cell styling based on value and type
  const getCellStyle = (item: ScannerData, key: string, value: any) => {
    if (dsOn) return getDsCellStyle(item, key, value);
    const cellPad = simpleDensityMetrics?.cellPadding ?? '6px 10px';
    const textCol = isDataTableTextColumn(key);
    const baseStyle: React.CSSProperties = {
      boxSizing: 'border-box',
      padding: cellPad,
      borderRight: '1px solid',
      borderBottom: '1px solid',
      transition: 'all 0.3s ease',
      textAlign: textCol ? 'start' : 'end',
      ...(textCol ? {} : { fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums' as const }),
      /** Do not set `position` here — it overrides `position:sticky` on frozen body cells and breaks column alignment with headers. */
      ...(simpleDensityMetrics
        ? { fontSize: simpleDensityMetrics.bodyFontPx, lineHeight: 1.28 }
        : { fontSize: 12.5, lineHeight: 1.3 }),
    };

    const rowIndex = paginatedData.indexOf(item);
    const isFlashing = flashingCells.has(`${rowIndex}-${key}`);
    const isPositionFlashing = flashingCells.has(`${rowIndex}-position`);
    
    const mix = (a: string, b: string, pct: number) => `color-mix(in srgb, ${a} ${pct}%, ${b})`;

    const flashingStyle = isFlashing ? {
      backgroundColor: colors.warning,
      color: colors.textInverse,
      fontWeight: 'bold',
      animation: 'flash 0.5s ease-in-out',
    } : {};
    
    const positionFlashingStyle = isPositionFlashing ? {
      backgroundColor: mix(colors.primary, colors.background, 42),
      color: colors.primary,
      fontWeight: 'bold',
      animation: 'pulse 1s ease-in-out',
      border: `2px solid ${colors.primary}`,
    } : {};

    // Color coding based on field type (palette-aligned, no fixed browns)
    let colorStyle = {};
    
    if (key.includes('Delta')) {
      if (value > 0) colorStyle = { backgroundColor: mix(colors.success, colors.background, 36), color: colors.success };
      else if (value < 0) colorStyle = { backgroundColor: mix(colors.error, colors.background, 36), color: colors.error };
    }
    
    if (key.includes('Volume')) {
      if (value > 1000) colorStyle = { backgroundColor: mix(colors.primary, colors.background, 30), color: colors.primary };
      else if (value > 100) colorStyle = { backgroundColor: mix(colors.warning, colors.background, 32), color: colors.warning };
    }
    
    // Technical indicators color coding - buy/sell signals
    if (key.includes('Haltz') || key.includes('Band') || key.includes('Mal') || key.includes('Zahn')) {
      if (value > 50) colorStyle = { backgroundColor: mix(colors.success, colors.background, 40), color: colors.success, fontWeight: 'bold' };
      else if (value > 10) colorStyle = { backgroundColor: mix(colors.success, colors.background, 26), color: colors.success };
      else if (value < -50) colorStyle = { backgroundColor: mix(colors.error, colors.background, 40), color: colors.error, fontWeight: 'bold' };
      else if (value < -10) colorStyle = { backgroundColor: mix(colors.error, colors.background, 26), color: colors.error };
      else colorStyle = { backgroundColor: mix(colors.textMuted, colors.background, 18), color: colors.textMuted };
    }
    
    if (key.includes('Option Type')) {
      if (value === 'C' || value === 'CALL') colorStyle = { backgroundColor: mix(colors.success, colors.background, 34), color: colors.success };
      else if (value === 'P' || value === 'PUT') colorStyle = { backgroundColor: mix(colors.error, colors.background, 34), color: colors.error };
    }

    return { ...baseStyle, ...colorStyle, ...flashingStyle, ...positionFlashingStyle };
  };

  // Get high/low value highlighting
  const getHighLowStyle = (key: string, value: number) => {
    if (typeof value !== 'number') return {};
    if (dsOn) return {};
    
    const values = paginatedData.map(item => item[key as keyof ScannerData]).filter(v => typeof v === 'number') as number[];
    const max = values.length > 0 ? Math.max(...values) : 0;
    const min = values.length > 0 ? Math.min(...values) : 0;
    
    const mixHl = (a: string, b: string, pct: number) => `color-mix(in srgb, ${a} ${pct}%, ${b})`;

    if (value === max) return { 
      backgroundColor: mixHl(colors.warning, colors.background, 38),
      color: colors.warning,
      fontWeight: 'bold',
      border: `2px solid ${colors.warning}`,
    };
    
    if (value === min) return { 
      backgroundColor: mixHl(colors.primary, colors.background, 34),
      color: colors.primary,
      fontWeight: 'bold',
      border: `2px solid ${colors.primary}`,
    };
    
    return {};
  };

  const onResizePointerMove = useCallback(
    (ev: PointerEvent) => {
      const drag = resizeDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      const minW = simpleLayout && simpleDensityMetrics ? simpleDensityMetrics.minColFloor : 50;
      const deltaX = language === 'ar' ? drag.startX - ev.clientX : ev.clientX - drag.startX;
      const newWidth = Math.max(minW, Math.round(drag.startW + deltaX));
      setColumnConfig((prev) => prev.map((c) => (c.key === drag.key ? { ...c, width: newWidth } : c)));
    },
    [simpleLayout, simpleDensityMetrics, language],
  );

  const onResizePointerUp = useCallback(
    (ev: PointerEvent) => {
      const drag = resizeDragRef.current;
      if (!drag || ev.pointerId !== drag.pointerId) return;
      window.removeEventListener('pointermove', onResizePointerMove);
      window.removeEventListener('pointerup', onResizePointerUp);
      window.removeEventListener('pointercancel', onResizePointerUp);
      resizeDragRef.current = null;
      try {
        resizeCaptureElRef.current?.releasePointerCapture(ev.pointerId);
      } catch {
        /* ignore */
      }
      resizeCaptureElRef.current = null;
      setIsResizing(false);
      setResizingColumn(null);
    },
    [onResizePointerMove],
  );

  const handleResizePointerDown = useCallback(
    (e: React.PointerEvent, columnKey: string) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const column = columnConfig.find((c) => c.key === columnKey);
      if (!column) return;
      const startW = getColumnDisplayWidth(column);
      resizeDragRef.current = {
        key: columnKey,
        startX: e.clientX,
        startW,
        pointerId: e.pointerId,
      };
      resizeCaptureElRef.current = e.currentTarget as HTMLElement;
      try {
        resizeCaptureElRef.current.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      setIsResizing(true);
      setResizingColumn(columnKey);
      window.addEventListener('pointermove', onResizePointerMove);
      window.addEventListener('pointerup', onResizePointerUp);
      window.addEventListener('pointercancel', onResizePointerUp);
    },
    [columnConfig, getColumnDisplayWidth, onResizePointerMove, onResizePointerUp],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', onResizePointerMove);
      window.removeEventListener('pointerup', onResizePointerUp);
      window.removeEventListener('pointercancel', onResizePointerUp);
      resizeDragRef.current = null;
    };
  }, [onResizePointerMove, onResizePointerUp]);

  // Column customization handlers (add unknown keys to config if not present)
  const toggleColumnVisibility = (key: string) => {
    setColumnConfig(prev => {
      const found = prev.find(col => col.key === key);
      if (found) return prev.map(col => col.key === key ? { ...col, visible: !col.visible } : col);
      return [...prev, { key, label: key, visible: true, order: prev.length + 1, width: COLUMN_WIDTH_CATALOG_DEFAULT }];
    });
  };

  const moveColumn = (key: string, direction: 'up' | 'down') => {
    setColumnConfig(prev => {
      const newConfig = [...prev];
      const index = newConfig.findIndex(col => col.key === key);
      
      if (direction === 'up' && index > 0) {
        [newConfig[index], newConfig[index - 1]] = [newConfig[index - 1], newConfig[index]];
      } else if (direction === 'down' && index < newConfig.length - 1) {
        [newConfig[index], newConfig[index + 1]] = [newConfig[index + 1], newConfig[index]];
      }
      
      // Update order numbers
      newConfig.forEach((col, idx) => {
        col.order = idx + 1;
      });
      
      return newConfig;
    });
  };

  const resetToDefault = () => {
    setColumnConfig(
      buildDefaultColumns(
        language,
        dataSource === 'stable',
        dataSource === 'rtl_legacy' ? effectiveDataMode : 'accumulated',
      ),
    );
  };

  const handleScannerDataModeChange = useCallback(
    (mode: DataMode) => {
      setDataMode(mode);
      try {
        localStorage.setItem('mmec_rtl_data_mode', mode);
      } catch {
        /* ignore */
      }
      window.dispatchEvent(new CustomEvent('mmecDataModeChanged', { detail: { mode } }));
    },
    [setDataMode],
  );

  const themeColors = useMemo(() => {
    const mix = (a: string, b: string, pct: number) => `color-mix(in srgb, ${a} ${pct}%, ${b})`;
    return {
      /** Page / scrollbar track (neutral base, not concept-brown surface). */
      background: colors.background,
      /** Zebra stripes: even = canvas, odd = subtle primary tint (matches palette, not surfaceHover hue). */
      tableStripeEven: colors.background,
      tableStripeOdd: mix(colors.primary, colors.background, 11),
      rowHover: mix(colors.primary, colors.background, 20),
      text: colors.text,
      border: colors.surfaceBorder,
      header: colors.surface,
      row: colors.surfaceHover,
      accent: colors.chartPrimary,
      frozenBorder: colors.accent,
      success: colors.success,
      danger: colors.error,
      warning: colors.warning,
      info: colors.info,
      ...(isDesignSystemOn()
        ? {
            background: 'var(--ground)',
            tableStripeEven: 'var(--ground)',
            tableStripeOdd: 'var(--ground)',
            rowHover: 'var(--surface-3)',
            border: 'var(--line)',
            header: 'var(--ground-pane)',
            frozenBorder: 'var(--line-strong)',
            accent: 'var(--info)',
          }
        : {}),
    };
  }, [colors]);

  const syncHorizontalFromMain = useCallback(() => {
    const main = tableScrollRef.current;
    const top = hScrollTopRef.current;
    const bot = hScrollBottomRef.current;
    if (!main) return;
    const sl = main.scrollLeft;
    if (top && top.scrollLeft !== sl) top.scrollLeft = sl;
    if (bot && bot.scrollLeft !== sl) bot.scrollLeft = sl;
  }, []);

  const setMainHorizontalScroll = useCallback(
    (scrollLeft: number) => {
      const main = tableScrollRef.current;
      if (!main || main.scrollLeft === scrollLeft) return;
      main.scrollLeft = scrollLeft;
      syncHorizontalFromMain();
    },
    [syncHorizontalFromMain]
  );

  useLayoutEffect(() => {
    const main = tableScrollRef.current;
    const topInner = hScrollTopInnerRef.current;
    const botInner = hScrollBottomInnerRef.current;
    if (!main || !topInner || !botInner) return;

    const updateSpacers = () => {
      const w = `${Math.max(main.scrollWidth, 1)}px`;
      topInner.style.width = w;
      botInner.style.width = w;
    };

    updateSpacers();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updateSpacers);
    });
    ro.observe(main);
    const first = main.firstElementChild;
    if (first) ro.observe(first);

    return () => ro.disconnect();
  }, [
    paginatedData,
    consolidatedView,
    dataSource,
    frozenColumns,
    scrollableColumns,
    effectiveColumnConfig,
    filteredData.length,
    resizingColumn,
    isResizing,
    itemsPerPage,
    currentPage,
    sortConfig,
    simpleTableDensity,
    simpleLayout,
  ]);

  /** MMEC-aligned chrome for toolbar, chips, and pagination (semantic colors, no dynamic Tailwind). */
  const dtChrome = useMemo(
    () => {
    const dtBase = {
      bar: {
        padding: 16,
        borderBottom: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.surface,
      } as React.CSSProperties,
      foot: {
        padding: 16,
        borderTop: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.surface,
      } as React.CSSProperties,
      chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 10px',
        borderRadius: 8,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: isDarkMode
          ? `color-mix(in srgb, ${colors.primary} 26%, ${colors.surface})`
          : `color-mix(in srgb, ${colors.primary} 14%, ${colors.surface})`,
        color: colors.primary,
        border: `1px solid ${colors.surfaceBorder}`,
      } as React.CSSProperties,
      label: { fontSize: 13, fontWeight: 600, color: colors.text } as React.CSSProperties,
      hint: { fontSize: 12, color: colors.textMuted } as React.CSSProperties,
      /** Toolbar / filter toggles: one neutral outline off-state; on-state always brand primary (no green/blue/red mix). */
      outlineBtn: (active: boolean): React.CSSProperties => {
        const base: React.CSSProperties = {
          padding: '4px 8px',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
        };
        if (!active) {
          return {
            ...base,
            backgroundColor: colors.background,
            color: colors.text,
            border: `1px solid ${colors.surfaceBorder}`,
          };
        }
        return {
          ...base,
          backgroundColor: colors.primary,
          color: colors.textInverse,
          border: `1px solid ${colors.primary}`,
        };
      },
      lgBtn: (bg: string, fg: string): React.CSSProperties => ({
        padding: '4px 10px',
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        border: `1px solid ${bg}`,
        cursor: 'pointer',
        backgroundColor: bg,
        color: fg,
      }),
      select: {
        padding: '8px 12px',
        borderRadius: 10,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.background,
        color: colors.text,
        fontSize: 13,
      } as React.CSSProperties,
      inputSm: {
        width: 128,
        padding: '4px 8px',
        fontSize: 12,
        borderRadius: 8,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.background,
        color: colors.text,
      } as React.CSSProperties,
      /** Compact data-table filters: align with toolbar outline buttons (~26px tall). */
      filterCtrl: {
        padding: '2px 6px',
        fontSize: 11,
        lineHeight: 1.2,
        borderRadius: 6,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.background,
        color: colors.text,
        minHeight: 26,
        boxSizing: 'border-box' as const,
      } as React.CSSProperties,
      filterLabel: {
        fontSize: 10,
        fontWeight: 600,
        color: colors.textMuted,
        marginBottom: 2,
        display: 'block',
      } as React.CSSProperties,
      panel: {
        padding: 16,
        borderRadius: 12,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.surface,
      } as React.CSSProperties,
      subPanel: {
        marginBottom: 16,
        padding: 16,
        borderRadius: 10,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.background,
      } as React.CSSProperties,
      railSection: {
        padding: 12,
        borderRadius: 10,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.background,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
        boxSizing: 'border-box' as const,
      } as React.CSSProperties,
      railSectionTitle: {
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase' as const,
        color: colors.textMuted,
      } as React.CSSProperties,
      sortBtn: {
        marginLeft: 4,
        padding: '2px 4px',
        color: colors.textMuted,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        fontSize: 10,
        borderRadius: 6,
      } as React.CSSProperties,
      pageNavBtn: (disabled: boolean): React.CSSProperties => ({
        padding: '8px 14px',
        borderRadius: 10,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.background,
        color: colors.text,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        fontSize: 13,
        fontWeight: 600,
      }),
      smOutlineDisabled: (disabled: boolean): React.CSSProperties => ({
        padding: '4px 8px',
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 8,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.surface,
        color: colors.text,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
      }),
      flyout: {
        position: 'fixed' as const,
        top: 16,
        width: 384,
        maxHeight: '24rem',
        overflowY: 'auto' as const,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.surfaceBorder}`,
        borderRadius: 12,
        boxShadow: 'var(--lift-2)',
        zIndex: 50,
      } as React.CSSProperties,
      inputFull: {
        width: '100%',
        boxSizing: 'border-box' as const,
        padding: '10px 12px',
        borderRadius: 10,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.background,
        color: colors.text,
        fontSize: 13,
      } as React.CSSProperties,
      inputFullXs: {
        width: '100%',
        boxSizing: 'border-box' as const,
        padding: '6px 8px',
        fontSize: 12,
        borderRadius: 8,
        border: `1px solid ${colors.surfaceBorder}`,
        backgroundColor: colors.background,
        color: colors.text,
      } as React.CSSProperties,
      fieldLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 8 } as React.CSSProperties,
      fieldLabelTight: { display: 'block', fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 4 } as React.CSSProperties,
      fieldLabelXs: { display: 'block', fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 4 } as React.CSSProperties,
      fieldHint: { fontSize: 12, color: colors.textMuted, marginTop: 4 } as React.CSSProperties,
      sectionTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: colors.text,
        borderBottom: `1px solid ${colors.surfaceBorder}`,
        paddingBottom: 8,
        marginBottom: 12,
      } as React.CSSProperties,
      sectionTitleSm: { fontSize: 14, fontWeight: 600, color: colors.text, marginBottom: 12 } as React.CSSProperties,
      rangeTrack: {
        width: '100%',
        height: 8,
        borderRadius: 8,
        backgroundColor: 'var(--border)',
        cursor: 'pointer',
        accentColor: colors.primary,
      } as React.CSSProperties,
    };
    if (!isDesignSystemOn()) return dtBase;
    /* Design System preview: toolbar + filter rail as Pill sm / Button ghost / Input, hairline cards. */
    const dsCtrl: React.CSSProperties = {
      height: 30,
      padding: '0 8px',
      fontSize: 12,
      lineHeight: '28px',
      borderRadius: 4,
      border: '1px solid var(--line-strong)',
      backgroundColor: 'var(--surface-2)',
      color: 'var(--text)',
      boxSizing: 'border-box',
      colorScheme: 'dark',
    };
    const dsGhost: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: 28,
      padding: '0 10px',
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      border: '1px solid var(--line)',
      backgroundColor: 'transparent',
      color: 'var(--text)',
      cursor: 'pointer',
      transition: 'background-color 150ms, border-color 150ms',
    };
    return {
      ...dtBase,
      bar: { padding: 12, borderBottom: '1px solid var(--line)', backgroundColor: 'var(--ground-pane)' } as React.CSSProperties,
      foot: { padding: '8px 16px', borderTop: '1px solid var(--line)', backgroundColor: 'var(--ground)' } as React.CSSProperties,
      chip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        height: 20,
        padding: '0 8px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 700,
        fontFamily: 'var(--font-mono)',
        backgroundColor: 'var(--surface-3)',
        color: 'var(--text)',
        border: 'none',
      } as React.CSSProperties,
      label: { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' } as React.CSSProperties,
      hint: { fontSize: 11, color: 'var(--text-muted)' } as React.CSSProperties,
      outlineBtn: (active: boolean): React.CSSProperties => ({
        display: 'inline-flex',
        alignItems: 'center',
        height: 24,
        padding: '0 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background-color 150ms, border-color 150ms, color 150ms',
        backgroundColor: active ? 'var(--gold-wash)' : 'var(--surface-2)',
        border: `1px solid ${active ? 'var(--gold)' : 'var(--line)'}`,
        color: 'var(--text)',
      }),
      lgBtn: (_bg: string, _fg: string): React.CSSProperties => dsGhost,
      select: dsCtrl,
      inputSm: { ...dsCtrl, width: 140 },
      filterCtrl: dsCtrl,
      filterLabel: { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 4, display: 'block' } as React.CSSProperties,
      panel: { padding: 16, borderRadius: 8, border: '1px solid var(--line)', backgroundColor: 'var(--surface-2)' } as React.CSSProperties,
      subPanel: { marginBottom: 16, padding: 12, borderRadius: 8, border: '1px solid var(--line)', backgroundColor: 'var(--ground-pane)' } as React.CSSProperties,
      railSection: {
        ...dtBase.railSection,
        padding: 12,
        borderRadius: 8,
        border: '1px solid var(--line)',
        backgroundColor: 'var(--surface-2)',
      } as React.CSSProperties,
      railSectionTitle: { ...dtBase.railSectionTitle, fontSize: 10, letterSpacing: language === 'ar' ? 0 : '0.08em', textTransform: language === 'ar' ? 'none' : 'uppercase', color: 'var(--text-muted)' } as React.CSSProperties,
      sortBtn: { ...dtBase.sortBtn, color: 'var(--text-dim)', borderRadius: 2 } as React.CSSProperties,
      pageNavBtn: (disabled: boolean): React.CSSProperties => ({ ...dsGhost, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1 }),
      smOutlineDisabled: (disabled: boolean): React.CSSProperties => ({ ...dsGhost, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1 }),
      flyout: { ...dtBase.flyout, backgroundColor: 'var(--surface-panel)', border: '1px solid var(--line)', borderRadius: 8, boxShadow: 'var(--shadow-panel)' } as React.CSSProperties,
      inputFull: { ...dsCtrl, width: '100%', height: 32 } as React.CSSProperties,
      inputFullXs: { ...dsCtrl, width: '100%' } as React.CSSProperties,
      rangeTrack: { ...dtBase.rangeTrack, accentColor: 'var(--gold)' } as React.CSSProperties,
    };
    },
    [colors, isDarkMode, language]
  );

  /** Right-rail pager: rows-per-page on its own row; page numbers + nav on the next (never mixed with tool icons). */
  const dataTablePaginationRail = useMemo(
    () => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
        <div>
          <label htmlFor="mmec-dt-rows-per-page" style={{ ...dtChrome.filterLabel, marginBottom: 6 }}>
            {language === 'ar' ? 'صفوف لكل صفحة' : 'Rows per page'}
          </label>
          <select
            id="mmec-dt-rows-per-page"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ ...dtChrome.select, padding: '8px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
        <div>
          <span style={{ ...dtChrome.filterLabel, marginBottom: 6, display: 'block' }}>
            {language === 'ar' ? 'الصفحات' : 'Pages'}
          </span>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 6,
              rowGap: 6,
            }}
          >
            <button
              type="button"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage <= 1}
              style={{ ...dtChrome.outlineBtn(false), padding: '4px 10px', opacity: currentPage <= 1 ? 0.45 : 1 }}
              title={language === 'ar' ? 'الأولى' : 'First'}
            >
              «
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              style={{ ...dtChrome.outlineBtn(false), padding: '4px 10px', opacity: currentPage <= 1 ? 0.45 : 1 }}
              title={language === 'ar' ? 'السابقة' : 'Previous'}
            >
              ‹
            </button>
            {pageListWithGaps.map((item, idx) =>
              item === '…' ? (
                <span key={`ellipsis-${idx}`} style={{ padding: '0 2px', fontSize: 12, color: colors.textMuted, userSelect: 'none' }}>
                  …
                </span>
              ) : (
                <button
                  key={`p-${item}`}
                  type="button"
                  onClick={() => setCurrentPage(item)}
                  style={{ ...dtChrome.outlineBtn(currentPage === item), minWidth: 30, padding: '4px 8px', justifyContent: 'center' }}
                >
                  {item}
                </button>
              ),
            )}
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              style={{ ...dtChrome.outlineBtn(false), padding: '4px 10px', opacity: currentPage >= totalPages ? 0.45 : 1 }}
              title={language === 'ar' ? 'التالية' : 'Next'}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage >= totalPages}
              style={{ ...dtChrome.outlineBtn(false), padding: '4px 10px', opacity: currentPage >= totalPages ? 0.45 : 1 }}
              title={language === 'ar' ? 'الأخيرة' : 'Last'}
            >
              »
            </button>
          </div>
          <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 8, lineHeight: 1.4 }}>
            {language === 'ar' ? 'صفحة' : 'Page'} {currentPage} {language === 'ar' ? 'من' : 'of'} {totalPages}
            {' · '}
            {filteredData.length.toLocaleString()} {language === 'ar' ? 'صف مطابق' : 'matching rows'}
          </div>
        </div>
      </div>
    ),
    [
      currentPage,
      totalPages,
      pageListWithGaps,
      itemsPerPage,
      filteredData.length,
      language,
      colors.textMuted,
      dtChrome,
    ],
  );

  const renderMmecDataTableRailCore = () => (
    <>
      {/* Stable (CBOE): single underlying from Fetch bar. RTL: no underlying selector — all data by default. */}
      {(typeof window !== 'undefined' ? (localStorage.getItem('mmec_data_source') || 'rtl_legacy') : 'rtl_legacy') === 'stable' ? (
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span style={dtChrome.label}>{language === 'ar' ? 'البيانات المعروضة:' : 'Fetched:'}</span>
          <span style={dtChrome.chip}>
            {readStablePrimarySessionFirst() || (deferredData?.[0]?.Underlying ?? deferredData?.[0]?.Symbol) || '—'}
          </span>
          <span style={dtChrome.hint}>{language === 'ar' ? '(غيّر من الشريط أعلاه)' : '(change in the bar above)'}</span>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span style={dtChrome.label}>{language === 'ar' ? 'تتبع الأصول (اختياري):' : 'Track underlyings (optional):'}</span>
          {selectedUnderlyings.map((u) => (
            <span key={u} style={dtChrome.chip}>
              {u}
              <button
                type="button"
                onClick={() => handleUnderlyingToggle(u)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: colors.primary,
                  cursor: 'pointer',
                  padding: '0 2px',
                  borderRadius: 4,
                }}
                aria-label={language === 'ar' ? 'إزالة' : 'Remove'}
                title={language === 'ar' ? 'إزالة أو إضافة مرة أخرى' : 'Remove (or add again to toggle)'}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => handleSelectAllUnderlyings(false, [])}
            style={{ ...dtChrome.hint, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
          >
            {language === 'ar' ? 'مسح الكل' : 'Clear all'}
          </button>
          <span style={{ ...dtChrome.hint, opacity: 0.6 }}>|</span>
          <input
            type="text"
            value={underlyingInputValue}
            onChange={(e) => setUnderlyingInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const raw = (e.target as HTMLInputElement).value?.trim().toUpperCase() || '';
              if (!raw) return;
              setUnderlyingInputValue('');
              handleUnderlyingToggle(raw);
            }}
            list="rtl-underlying-suggestions"
            placeholder={language === 'ar' ? 'أضف رمزاً ثم Enter' : 'Add symbol, press Enter'}
            style={dtChrome.inputSm}
            title={language === 'ar' ? 'اكتب الرمز ثم Enter: إضافة أو إزالة' : 'Type symbol + Enter to add or remove (toggle)'}
          />
          <datalist id="rtl-underlying-suggestions">
            {(() => {
              const fromData = deferredData?.length
                ? Array.from(new Set(deferredData.map((r) => String(r.Underlying || '').trim()).filter(Boolean))).slice(0, 40)
                : [];
              const combined = Array.from(new Set([...(apiUnderlyings || []), ...fromData])).sort();
              return combined.map((s) => <option key={s} value={s} />);
            })()}
          </datalist>
        </div>
      )}

      {(dataSource === 'rtl_legacy' || dataSource === 'stable') && lastLevel2Totals && (
        <div className="flex flex-wrap items-center gap-4 mb-2" style={{ fontSize: 13, color: colors.textSecondary }}>
          <span style={{ fontWeight: 700, color: colors.text }}>{language === 'ar' ? 'المستوى 2' : 'Level 2'}:</span>
          <span>
            Bid size:{' '}
            <strong>
              {lastLevel2Totals.total_bid_volume > 0
                ? lastLevel2Totals.total_bid_volume.toLocaleString()
                : language === 'ar'
                  ? 'بيانات مفقودة'
                  : 'Missing data'}
            </strong>
          </span>
          <span>
            Ask size:{' '}
            <strong>
              {lastLevel2Totals.total_ask_volume > 0
                ? lastLevel2Totals.total_ask_volume.toLocaleString()
                : language === 'ar'
                  ? 'بيانات مفقودة'
                  : 'Missing data'}
            </strong>
          </span>
          <span>
            Total OI:{' '}
            <strong>
              {lastLevel2Totals.total_open_interest != null && lastLevel2Totals.total_open_interest > 0
                ? lastLevel2Totals.total_open_interest.toLocaleString()
                : language === 'ar'
                  ? 'بيانات مفقودة'
                  : 'Missing data'}
            </strong>
          </span>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', marginBottom: 16 }}>
        <div style={dtChrome.railSection}>
          <div style={dtChrome.railSectionTitle}>{language === 'ar' ? 'الجدول' : 'Table'}</div>
          <div className="flex flex-wrap items-center gap-1">
            <button type="button" onClick={() => setColumnPickerOpen(true)} style={dtChrome.outlineBtn(columnPickerOpen)}>
              {language === 'ar' ? 'الأعمدة' : 'Columns'}
            </button>
            <button type="button" onClick={resetToDefault} style={dtChrome.outlineBtn(false)}>
              {language === 'ar' ? 'إعادة الأعمدة' : 'Reset cols'}
            </button>
            {dataSource === 'rtl_legacy' && (
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: colors.textSecondary,
                  marginLeft: 4,
                }}
              >
                <span>{language === 'ar' ? 'وضع البيانات' : 'Data mode'}</span>
                <select
                  value={effectiveDataMode}
                  onChange={(e) => handleScannerDataModeChange(e.target.value as DataMode)}
                  style={{ ...dtChrome.filterCtrl, minWidth: 108 }}
                  aria-label={language === 'ar' ? 'وضع البيانات' : 'Data mode'}
                >
                  <option value="accumulated">{language === 'ar' ? 'التراكمي' : 'Accumulated'}</option>
                  <option value="5min">{language === 'ar' ? '5 دقائق' : '5 Minutes'}</option>
                </select>
              </label>
            )}
            {(dataSource === 'stable' || dataSource === 'rtl_legacy') && (
              <button
                type="button"
                onClick={() => setConsolidatedView((v) => !v)}
                style={dtChrome.outlineBtn(consolidatedView)}
                title={
                  language === 'ar'
                    ? 'عرض صف لكل سعر التنفيذ مع أشرطة صافية (دلتا، جاما، …)'
                    : 'One row per strike with net metrics and bidirectional bars'
                }
              >
                {language === 'ar' ? 'مُجمَّع' : 'Consolidated'}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setAdvancedFilterDraft(cloneFilterGroups(filterGroups));
                setAdvancedFilterModalOpen(true);
              }}
              style={dtChrome.outlineBtn(advancedFilterModalOpen)}
            >
              {language === 'ar' ? 'متقدم' : 'Advanced'}
              {hasActiveAdvancedFilters && (
                <span
                  style={{
                    marginLeft: 6,
                    padding: '1px 6px',
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    backgroundColor: colors.primary,
                    color: colors.textInverse,
                  }}
                >
                  {language === 'ar' ? '●' : 'on'}
                </span>
              )}
            </button>
          </div>
        </div>

        <div style={dtChrome.railSection}>
          <div style={dtChrome.railSectionTitle}>{language === 'ar' ? 'التنبيهات' : 'Alerts'}</div>
          <div className="flex flex-wrap items-center gap-1">
            <button type="button" onClick={() => setSoundEnabled(!soundEnabled)} style={dtChrome.outlineBtn(soundEnabled)} title="Sound">
              🔊 {soundEnabled ? 'ON' : 'OFF'}
            </button>
            <button type="button" onClick={() => setAlertsEnabled(!alertsEnabled)} style={dtChrome.outlineBtn(alertsEnabled)} title="Alerts">
              🔔 {alertsEnabled ? 'ON' : 'OFF'}
            </button>
            <button type="button" onClick={() => setShowAlerts(!showAlerts)} style={dtChrome.outlineBtn(showAlerts)}>
              📊 ({alertNotifications.length})
            </button>
            <button type="button" onClick={() => setShowAlertSettings(!showAlertSettings)} style={dtChrome.outlineBtn(showAlertSettings)}>
              ⚙️
            </button>
          </div>
        </div>

        <div style={dtChrome.railSection}>
          <div style={dtChrome.railSectionTitle}>{language === 'ar' ? 'تصدير' : 'Export'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <div className="flex flex-wrap items-center gap-1">
              <button type="button" onClick={exportToCSV} style={dtChrome.lgBtn(colors.primary, colors.textInverse)}>
                CSV
              </button>
              <button type="button" onClick={exportSettings} style={dtChrome.lgBtn(colors.primary, colors.textInverse)}>
                Export
              </button>
              <label
                style={{ ...dtChrome.lgBtn(colors.primary, colors.textInverse), display: 'inline-block', cursor: 'pointer', margin: 0 }}
              >
                Import
                <input type="file" accept=".json" onChange={importSettings} className="hidden" />
              </label>
            </div>
            <span style={{ fontSize: 11, color: colors.textMuted, lineHeight: 1.35 }}>
              {language === 'ar' ? 'اسحب حدود الأعمدة لتغيير العرض' : 'Drag column borders to resize'}
            </span>
            <span style={{ fontSize: 12, color: colors.textSecondary, fontWeight: 600 }}>
              {language === 'ar' ? 'سجلات مطابقة:' : 'Matching rows:'} {filteredData.length}
            </span>
          </div>
        </div>

        <div style={dtChrome.railSection}>
          <div style={dtChrome.railSectionTitle}>{language === 'ar' ? 'ترقيم الصفحات' : 'Pagination'}</div>
          {dataTablePaginationRail}
        </div>

        <div style={dtChrome.railSection}>
          <div style={dtChrome.railSectionTitle}>{language === 'ar' ? 'مرشحات الصفوف' : 'Row filters'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, width: '100%' }}>
            <div>
              <span style={dtChrome.filterLabel}>{language === 'ar' ? 'الفئة' : 'Category'}</span>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as 'all' | 'stocks' | 'etfs' | 'indices')}
                style={{ ...dtChrome.filterCtrl, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
              >
                <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
                <option value="stocks">{language === 'ar' ? 'أسهم' : 'Stocks'}</option>
                <option value="etfs">ETFs</option>
                <option value="indices">{language === 'ar' ? 'مؤشرات' : 'Indices'}</option>
              </select>
            </div>
            <div>
              <span style={dtChrome.filterLabel}>{language === 'ar' ? 'الأصل' : 'Underlying'}</span>
              <input
                type="text"
                value={filters.Underlying || ''}
                onChange={(e) => setFilters((prev) => ({ ...prev, Underlying: e.target.value }))}
                placeholder={language === 'ar' ? 'رمز' : 'Symbol'}
                style={{ ...dtChrome.filterCtrl, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <span style={dtChrome.filterLabel}>{language === 'ar' ? 'سعر التنفيذ' : 'Strike'}</span>
              <input
                type="text"
                value={filters.Strike || ''}
                onChange={(e) => setFilters((prev) => ({ ...prev, Strike: e.target.value }))}
                placeholder="—"
                style={{ ...dtChrome.filterCtrl, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <span style={dtChrome.filterLabel}>{language === 'ar' ? 'نوع الخيار' : 'Call / put'}</span>
              <select
                value={filters['Option Type'] || ''}
                onChange={(e) => setFilters((prev) => ({ ...prev, 'Option Type': e.target.value }))}
                style={{ ...dtChrome.filterCtrl, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
              >
                <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                <option value="CALL">Call</option>
                <option value="PUT">Put</option>
              </select>
            </div>
            <div>
              <span style={dtChrome.filterLabel}>{language === 'ar' ? 'النقودية' : 'Moneyness'}</span>
              <select
                value={filters.Moneyness || ''}
                onChange={(e) => setFilters((prev) => ({ ...prev, Moneyness: e.target.value }))}
                style={{ ...dtChrome.filterCtrl, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
              >
                <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                <option value="ITM">ITM</option>
                <option value="ATM">ATM</option>
                <option value="OTM">OTM</option>
              </select>
            </div>
            <div>
              <span style={dtChrome.filterLabel}>{language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry'}</span>
              <select
                value={filters['Expiry Date'] || ''}
                onChange={(e) => setFilters((prev) => ({ ...prev, 'Expiry Date': e.target.value }))}
                style={{ ...dtChrome.filterCtrl, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
              >
                <option value="">{language === 'ar' ? 'الكل' : 'All'}</option>
                {availableExpiries.map((expiry) => (
                  <option key={expiry} value={expiry}>
                    {expiry}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <span style={dtChrome.filterLabel}>{language === 'ar' ? 'نطاق الأيام' : 'Days range'}</span>
              <select
                value={filters['Days Left'] || ''}
                onChange={(e) => {
                  const newRange = e.target.value;
                  setFilters((prev) => ({ ...prev, 'Days Left': newRange }));
                  try {
                    localStorage.setItem('mmec_expiry_range', newRange);
                  } catch {
                    /* ignore */
                  }
                  window.dispatchEvent(new CustomEvent('expiryRangeChanged', { detail: { range: newRange } }));
                }}
                style={{ ...dtChrome.filterCtrl, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
              >
                <option value="">{language === 'ar' ? 'كل الاستحقاقات' : 'All expiries'}</option>
                <option value="this-week">{language === 'ar' ? 'أسبوع' : 'This wk'}</option>
                <option value="weekly">{language === 'ar' ? 'أسبوعي' : 'Weekly'}</option>
                <option value="monthly">{language === 'ar' ? 'شهري' : 'Monthly'}</option>
                <option value="this-month">{language === 'ar' ? 'الشهر' : 'This mo'}</option>
                <option value="3-months">3 mo</option>
                <option value="quarter">Qtr</option>
                <option value="6-months">6 mo</option>
                <option value="annual">1 yr</option>
                <option value="specific-date">{language === 'ar' ? 'تاريخ' : 'Date'}</option>
                <option value="1">0DTE</option>
                <option value="2-3">2–3d</option>
                <option value="4-7">4–7d</option>
                <option value="8-14">1–2w</option>
                <option value="15-30">2–4w</option>
                <option value="31-90">1–3mo</option>
                <option value="90+">3mo+</option>
              </select>
            </div>
            {filters['Days Left'] === 'specific-date' && (
              <div>
                <span style={dtChrome.filterLabel}>{language === 'ar' ? 'تاريخ محدد' : 'Pick date'}</span>
                <input
                  type="date"
                  value={stableSpecificExpiryDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    setStableSpecificExpiryDate(v);
                    try {
                      localStorage.setItem('mmec_stable_specific_expiry', v);
                    } catch {
                      /* ignore */
                    }
                    window.dispatchEvent(new CustomEvent('expiryRangeChanged', { detail: { range: 'specific-date' } }));
                  }}
                  style={{ ...dtChrome.filterCtrl, width: '100%', minWidth: 0, boxSizing: 'border-box' }}
                />
              </div>
            )}
            <button type="button" onClick={clearAllFilters} style={{ ...dtChrome.outlineBtn(false), width: '100%', marginTop: 2 }}>
              {language === 'ar' ? 'مسح المرشحات' : 'Clear filters'}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // Design System preview (artboard web/states): skeleton rows and the in-card empty state.
  if (isDesignSystemOn() && isLoading && !data?.length) {
    return <ToolState kind="loading" lang={language === 'ar' ? 'ar' : 'en'} rows={10} />;
  }
  if (isDesignSystemOn() && !isLoading && (!data || data.length === 0)) {
    const dsStable = typeof window !== 'undefined' && localStorage.getItem('mmec_data_source') === 'stable';
    const ar = language === 'ar';
    return (
      <ToolState
        kind="empty"
        lang={ar ? 'ar' : 'en'}
        body={dsStable
          ? (ar ? 'اختر واحداً أو أكثر من الأصول أعلاه، أو انتقل إلى مصدر RTL لعرض كل البيانات دون تحديد.'
                : 'Select one or more underlyings above (Stable), or switch to RTL to see all data without selecting.')
          : undefined}
        meta={ar ? 'يتحدّث الجدول تلقائياً عند توفر البيانات' : 'The table refreshes on its own when data arrives'}
      />
    );
  }

  if (isLoading && !data?.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="animate-spin rounded-full h-32 w-32 border-b-2"
          style={{ borderBottomColor: themeColors.accent }}
        />
      </div>
    );
  }

  // Clear empty state when not loading and no data - explain what to do
  if (!isLoading && (!data || data.length === 0)) {
    const dataSource = typeof window !== 'undefined' ? (localStorage.getItem('mmec_data_source') || 'rtl_legacy') : 'rtl_legacy';
    const isStable = dataSource === 'stable';
    return (
      <div
        className="flex flex-col items-center justify-center h-64 gap-4 p-6 rounded-xl border"
        style={{
          borderColor: colors.surfaceBorder,
          backgroundColor: colors.surface,
          color: colors.text,
        }}
      >
        <p style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>
          {language === 'ar' ? 'لا توجد بيانات في الجدول' : 'No data in table'}
        </p>
        <p style={{ fontSize: 13, color: colors.textMuted, textAlign: 'center', maxWidth: 420 }}>
          {isStable
            ? (language === 'ar'
                ? 'اختر واحداً أو أكثر من الأصول أعلاه، أو انتقل إلى مصدر RTL لعرض كل البيانات دون تحديد.'
                : 'Select one or more underlyings above (Stable), or switch to RTL to see all data without selecting.')
            : (language === 'ar'
                ? 'في انتظار اتصال البيانات في الوقت الفعلي.'
                : 'Awaiting real-time data connection.')}
        </p>
        <p style={{ fontSize: 12, color: colors.textMuted }}>
          {language === 'ar' ? 'الجدول سيُحدّث تلقائياً عند توفر البيانات.' : 'Table will refresh automatically when data is available.'}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`w-full h-full flex flex-col relative${simpleLayout ? ' min-h-0 min-w-0 flex-1' : ''} mmec-dt${simpleLayout ? '' : ' mmec-dt--pro'}`}
    >
      
      
      {/* CSS for animations and resizing */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes flash {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.05); }
          }
          
          .resize-handle {
            position: absolute;
            top: 0;
            right: 0;
            width: 4px;
            height: 100%;
            cursor: col-resize;
            background: transparent;
            transition: background-color 0.2s ease;
          }
          
          .resize-handle:hover {
            background-color: ${themeColors.accent};
          }
          
          .resize-handle.active {
            background-color: ${themeColors.accent};
          }

          .data-table-mmec-resize-handle--touch {
            width: 16px;
            right: -6px;
            touch-action: none;
          }
          
          .table-container.resizing {
            user-select: none;
          }
          
          .table-container.resizing * {
            cursor: col-resize !important;
          }

          .data-table-mmec-row:hover td {
            background-color: ${themeColors.rowHover} !important;
          }
        `
      }} />
      
      {simpleLayout && simpleFilterOpen && (
        <button
          type="button"
          aria-label={language === 'ar' ? 'إغلاق' : 'Close'}
          onClick={() => setSimpleFilterOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 120,
            border: 'none',
            margin: 0,
            padding: 0,
            background: 'color-mix(in srgb, var(--void) 55%, transparent)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
          }}
        />
      )}
      {simpleLayout && (
        <aside
          aria-hidden={!simpleFilterOpen}
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            width: 'min(88vw, 320px)',
            zIndex: 130,
            backgroundColor: colors.surface,
            color: colors.text,
            borderRight: `1px solid ${colors.surfaceBorder}`,
            boxShadow: simpleFilterOpen ? 'var(--lift-3)' : 'none',
            transform: simpleFilterOpen ? 'translateX(0)' : 'translateX(-102%)',
            transition: 'transform 0.22s ease',
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: simpleFilterOpen ? 'auto' : 'none',
          }}
        >
          <div
            style={{
              padding: '12px 14px',
              borderBottom: `1px solid ${colors.surfaceBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 14 }}>{language === 'ar' ? 'المرشحات' : 'Filters'}</span>
            <button
              type="button"
              onClick={() => setSimpleFilterOpen(false)}
              style={{ ...mmecSimpleCloseBtnStyle, color: colors.textMuted }}
              aria-label="Close"
            >
              ×
            </button>
          </div>
          <div
            style={{
              overflow: 'auto',
              flex: 1,
              padding: 12,
              paddingLeft: `max(12px, env(safe-area-inset-left, 0px))`,
              paddingRight: `max(12px, env(safe-area-inset-right, 0px))`,
            }}
          >
            <div style={{ ...dtChrome.bar, borderBottom: 'none', padding: 0, backgroundColor: 'transparent' }}>
              {renderMmecDataTableRailCore()}
            </div>
          </div>
        </aside>
      )}
      {simpleLayout && (
        <button
          type="button"
          onClick={() => setSimpleFilterOpen((o) => !o)}
          aria-expanded={simpleFilterOpen}
          aria-label={language === 'ar' ? 'فتح المرشحات' : 'Open filters'}
          style={mmecSimpleFabStyle({
            left: 8,
            top: 12,
            primary: colors.primary,
            textInverse: colors.textInverse,
            border: colors.surfaceBorder,
          })}
        >
          ☰
        </button>
      )}

      {simpleLayout && (
        <div
          className={`flex flex-wrap items-center shrink-0 ${simpleDensityMetrics?.toolbarClassName ?? 'py-2 pl-14 pr-3 gap-2'}`}
          style={{
            borderBottom: `1px solid ${colors.surfaceBorder}`,
            backgroundColor: colors.surface,
            minHeight: simpleTableDensity === 'dense' ? 36 : simpleTableDensity === 'compact' ? 40 : 44,
          }}
        >
          {(dataSource === 'stable' || dataSource === 'rtl_legacy') && (
            <>
              <button
                type="button"
                onClick={() => setConsolidatedView(true)}
                style={dtChrome.outlineBtn(consolidatedView)}
              >
                {language === 'ar' ? 'مُجمَّع' : 'Consolidated'}
              </button>
              <button
                type="button"
                onClick={() => setConsolidatedView(false)}
                style={dtChrome.outlineBtn(!consolidatedView)}
              >
                {language === 'ar' ? 'جدول' : 'Table'}
              </button>
            </>
          )}
          <span
            className={`font-medium ${simpleTableDensity === 'dense' ? 'text-[10px]' : 'text-xs'}`}
            style={{ color: colors.textSecondary }}
          >
            {language === 'ar' ? 'صفوف:' : 'Rows:'} {filteredData.length}
          </span>
          <div className="ml-auto flex flex-wrap items-center gap-0.5 sm:gap-1">
            <span
              className={`font-semibold uppercase tracking-wide ${simpleTableDensity === 'dense' ? 'text-[9px]' : 'text-[10px]'}`}
              style={{ color: colors.textMuted }}
            >
              {language === 'ar' ? 'حجم الجدول' : 'Table size'}
            </span>
            {(
              [
                { id: 'dense' as const, short: 'A−', ar: 'صغير جداً', en: 'Dense (smallest)' },
                { id: 'compact' as const, short: 'A', ar: 'مدمج', en: 'Compact' },
                { id: 'standard' as const, short: 'A+', ar: 'عادي', en: 'Standard' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.id}
                type="button"
                title={language === 'ar' ? opt.ar : opt.en}
                aria-pressed={simpleTableDensity === opt.id}
                onClick={() => commitSimpleTableDensity(opt.id)}
                style={{
                  ...dtChrome.outlineBtn(simpleTableDensity === opt.id),
                  padding: simpleTableDensity === 'dense' ? '2px 6px' : '3px 8px',
                  fontSize: simpleTableDensity === 'dense' ? 10 : 11,
                  fontWeight: 700,
                  minWidth: simpleTableDensity === 'dense' ? 28 : 30,
                }}
              >
                {opt.short}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Controls Bar — full chrome (pro mode); portaled into MMEC right «Tool filters» rail when available */}
      {!simpleLayout && (
        <ProToolFilterSlot>
          <div
            style={{
              ...dtChrome.bar,
              fontFamily: 'var(--font-ui, "IBM Plex Sans", sans-serif)',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
              {renderMmecDataTableRailCore()}
          </div>
        </ProToolFilterSlot>
      )}

      {/* Table Container: top + bottom horizontal scroll mirrors (synced) so users need not scroll to the grid bottom only to move sideways */}
      <div className={`flex min-h-0 flex-1 flex-col overflow-hidden${simpleLayout ? ' min-w-0' : ''}`}>
        <div
          ref={hScrollTopRef}
          className={`shrink-0 overflow-x-auto overflow-y-hidden${simpleLayout ? ' min-w-0' : ''}`}
          style={{
            height: 14,
            borderBottom: `1px solid ${themeColors.border}`,
            scrollbarWidth: 'thin',
            scrollbarColor: `${themeColors.border} ${themeColors.background}`,
          }}
          onScroll={(e) => setMainHorizontalScroll(e.currentTarget.scrollLeft)}
          title={language === 'ar' ? 'تمرير أفقي (أعلى)' : 'Horizontal scroll (top)'}
          aria-label={language === 'ar' ? 'تمرير أفقي أعلى الجدول' : 'Top horizontal scroll for table'}
        >
          <div ref={hScrollTopInnerRef} style={{ height: 1, width: 1 }} />
        </div>
        <div
          ref={tableScrollRef}
          className={`table-container min-h-0 overflow-auto ${isResizing ? 'resizing' : ''}${simpleLayout ? ` min-w-0 flex-1 min-h-0 ${MMEC_SIMPLE_SCROLL_TABLE_WRAP_CLASS}` : ' h-full min-h-0'}`}
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: `${themeColors.border} ${themeColors.background}`,
          }}
          onScroll={syncHorizontalFromMain}
        >
          {consolidatedView && (dataSource === 'stable' || dataSource === 'rtl_legacy') ? (
            <DataTableConsolidatedView
              rows={filteredData}
              mode={dataSource === 'stable' ? 'stable' : 'rtl_legacy'}
              language={language}
              spot={consolidatedSpot}
              rtlDataMode={effectiveDataMode}
              groupBy={consolidatedGroupBy}
              onGroupByChange={setConsolidatedGroupBy}
              colors={{
                background: colors.background,
                surface: colors.surface,
                surfaceBorder: colors.surfaceBorder,
                text: colors.text,
                textSecondary: colors.textSecondary,
                textMuted: colors.textMuted,
                primary: colors.primary,
                chartPrimary: colors.chartPrimary,
                error: colors.error,
                warning: colors.warning,
              }}
              openHistorical={(p) => openHistorical(p, simpleLayout ? { compactChrome: true } : undefined)}
              onOpenInAnalyticsHub={
                dataSource === 'stable'
                  ? (row, columnKey) => openContractInAnalyticsHub(historicalParamsFromScannerRow(row, columnKey))
                  : undefined
              }
              onRowClick={onRowClick}
              simpleDensity={simpleLayout ? simpleTableDensity : undefined}
            />
          ) : (
          <table
            className={`border-separate border-spacing-0 ${simpleLayout ? 'w-max' : 'w-full'} mmec-dt__table`}
            style={{
              minWidth: 'max-content',
              /* simple: width from columns so the grid extends past the viewport and horizontal scroll works; w-full+fixed squashes cols on narrow flex parents */
              tableLayout: 'fixed',
            }}
          >
            <colgroup>
              {frozenColumns.map((col) => (
                <col key={`c-${col.key}`} style={{ width: getColumnDisplayWidth(col) }} />
              ))}
              {scrollableColumns.map((col) => (
                <col key={`c-${col.key}`} style={{ width: getColumnDisplayWidth(col) }} />
              ))}
              <col style={{ width: 36 }} />
            </colgroup>
            {/* Header — same padding/box model as body cells so columns line up */}
            <thead>
              <tr>
                {frozenColumns.map((col, fi) => {
                  const cw = getColumnDisplayWidth(col);
                  return (
                  <th
                    key={col.key}
                    className="relative border-r-2"
                    style={{
                      position: 'sticky',
                      top: 0,
                      left: frozenStickyLefts[fi] ?? 0,
                      zIndex: 40,
                      boxSizing: 'border-box',
                      padding: simpleDensityMetrics?.headerPadding ?? '6px 10px',
                      verticalAlign: 'middle',
                      borderRightColor: themeColors.frozenBorder,
                      backgroundColor: themeColors.header,
                      minWidth: cw,
                      maxWidth: cw,
                      width: cw,
                    }}
                  >
                    <div
                      className="flex items-center gap-1 min-w-0"
                      style={{ justifyContent: isDataTableTextColumn(col.key) ? 'space-between' : 'flex-end' }}
                    >
                      <span
                        className="min-w-0 whitespace-normal break-words hyphens-auto mmec-dt__th"
                        style={{
                          fontSize: simpleDensityMetrics?.headerLabelPx ?? 11,
                          fontWeight: 600,
                          letterSpacing: '0.03em',
                          lineHeight: 1.2,
                          color: sortConfig?.key === col.key ? 'var(--txt)' : 'var(--muted)',
                        }}
                      >
                        {col.label}
                        {headerSuffix[col.key] ? (
                          <span style={{ opacity: 0.6, fontWeight: 400 }}> {headerSuffix[col.key]}</span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        style={{
                          ...dtChrome.sortBtn,
                          fontSize: simpleDensityMetrics?.sortBtnFontPx ?? 10,
                          marginLeft: 4,
                          flexShrink: 0,
                          color: sortConfig?.key === col.key ? 'var(--acc)' : undefined,
                        }}
                        aria-label="Sort"
                      >
                        {sortConfig?.key === col.key ? (
                          sortConfig.direction === 'asc' ? '↑' : '↓'
                        ) : '↕'}
                      </button>
                    </div>
                    {/* Resize handle */}
                    <div
                      className={`resize-handle ${simpleLayout ? 'data-table-mmec-resize-handle--touch' : ''} ${resizingColumn === col.key ? 'active' : ''}`}
                      onPointerDown={(e) => handleResizePointerDown(e, col.key)}
                    />
                  </th>
                  );
                })}
                
                {/* Scrollable headers: must be sticky on each th (sticky on tr is unreliable; Volume/OI/Premium were scrolling away). */}
                {scrollableColumns.map((col) => {
                  const cw = getColumnDisplayWidth(col);
                  return (
                  <th
                    key={col.key}
                    className="relative border-r"
                    title={headerNote[col.key]}
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 25,
                      boxSizing: 'border-box',
                      padding: simpleDensityMetrics?.headerPadding ?? '6px 10px',
                      verticalAlign: 'middle',
                      borderColor: themeColors.border,
                      backgroundColor: themeColors.header,
                      minWidth: cw,
                      maxWidth: cw,
                      width: cw,
                    }}
                  >
                    <div
                      className="flex items-center gap-1 min-w-0"
                      style={{ justifyContent: isDataTableTextColumn(col.key) ? 'space-between' : 'flex-end' }}
                    >
                      <span
                        className="min-w-0 whitespace-normal break-words hyphens-auto mmec-dt__th"
                        style={{
                          fontSize: simpleDensityMetrics?.headerLabelPx ?? 11,
                          fontWeight: 600,
                          letterSpacing: '0.03em',
                          lineHeight: 1.2,
                          color: sortConfig?.key === col.key ? 'var(--txt)' : 'var(--muted)',
                        }}
                      >
                        {col.label}
                        {headerSuffix[col.key] ? (
                          <span style={{ opacity: 0.6, fontWeight: 400 }}> {headerSuffix[col.key]}</span>
                        ) : null}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        style={{
                          ...dtChrome.sortBtn,
                          fontSize: simpleDensityMetrics?.sortBtnFontPx ?? 10,
                          marginLeft: 4,
                          flexShrink: 0,
                          color: sortConfig?.key === col.key ? 'var(--acc)' : undefined,
                        }}
                        aria-label="Sort"
                      >
                        {sortConfig?.key === col.key ? (
                          sortConfig.direction === 'asc' ? '↑' : '↓'
                        ) : '↕'}
                      </button>
                    </div>
                    {/* Resize handle */}
                    <div
                      className={`resize-handle ${simpleLayout ? 'data-table-mmec-resize-handle--touch' : ''} ${resizingColumn === col.key ? 'active' : ''}`}
                      onPointerDown={(e) => handleResizePointerDown(e, col.key)}
                    />
                  </th>
                  );
                })}
                {/* Track column header */}
                <th
                  style={{
                    position: 'sticky', top: 0, zIndex: 25,
                    boxSizing: 'border-box', padding: '4px 6px',
                    verticalAlign: 'middle',
                    borderColor: themeColors.border,
                    backgroundColor: themeColors.header,
                    minWidth: 36, maxWidth: 36, width: 36,
                    textAlign: 'center',
                  }}
                >
                  <span style={{ fontSize: 12 }} title="Track contract">⭐</span>
                </th>
              </tr>
            </thead>

            {/* Body - Using memoized rows for better performance */}
            <tbody style={{ opacity: isDataStale ? 0.7 : 1, transition: 'opacity 0.2s' }}>
              {paginatedData.map((item, rowIndex) => (
                <TableRow
                  key={`${item.Underlying}-${item.Strike}-${item['Option Type']}-${rowIndex}`}
                  item={item}
                  rowIndex={rowIndex}
                  frozenColumns={frozenColumns}
                  scrollableColumns={scrollableColumns}
                  frozenStickyLefts={frozenStickyLefts}
                  getColumnDisplayWidth={getColumnDisplayWidth}
                  getCellValue={getCellValue}
                  getCellStyle={getCellStyle}
                  getHighLowStyle={getHighLowStyle}
                  themeColors={themeColors}
                  isDarkMode={isDarkMode}
                  simpleLayoutDensityKey={simpleLayout ? simpleTableDensity : null}
                  onCellClick={(rowItem, columnKey) => {
                    if (onRowClick) {
                      const u = String(rowItem.Underlying || '').replace(/^\$/, '').trim();
                      if (u) onRowClick(u);
                      return;
                    }
                    const expiry = getExpiryFromRecord(rowItem) || (rowItem['Expiry Date'] as string) || '';
                    const expiryStr = typeof expiry === 'string' ? expiry : (expiry && (expiry as any).toISOString?.()) ? (expiry as Date).toISOString().slice(0, 10) : '';
                    const source = columnKeyToHistoricalSource(columnKey);
                    openHistorical(
                      {
                        underlying: String(rowItem.Underlying || ''),
                        strike: (Number(rowItem.Strike) || rowItem.Strike) ?? '',
                        expiry: expiryStr,
                        optionType: ((rowItem['Option Type'] as string) || 'CALL') as 'CALL' | 'PUT',
                        source,
                      },
                      simpleLayout ? { compactChrome: true } : undefined
                    );
                  }}
                  onCellContextMenu={
                    dataSource === 'stable'
                      ? (rowItem, columnKey) => {
                          openContractInAnalyticsHub(historicalParamsFromScannerRow(rowItem, columnKey));
                        }
                      : undefined
                  }
                  trackCell={
                    <td
                      style={{
                        padding: '2px 4px',
                        borderBottom: `1px solid ${themeColors.border}`,
                        borderRight: `1px solid ${themeColors.border}`,
                        backgroundColor: rowIndex % 2 === 0 ? themeColors.tableStripeEven : themeColors.tableStripeOdd,
                        textAlign: 'center',
                        verticalAlign: 'middle',
                        minWidth: 36, maxWidth: 36, width: 36,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <TrackButton
                        symbol={String(item.Underlying || '')}
                        strike={Number(item.Strike) || 0}
                        expiry={(getExpiryFromRecord(item) || String(item['Expiry Date'] || '')).slice(0, 10)}
                        optionType={String(item['Option Type'] || 'C')}
                        lastPrice={Number(item.Last) || undefined}
                        lastVolume={Number(item.Volume) || undefined}
                        lastOI={Number((item as any)['Open Interest'] ?? (item as any).open_interest) || undefined}
                        lastBid={Number((item as any).Bid ?? (item as any).bid) || undefined}
                        lastAsk={Number((item as any).Ask ?? (item as any).ask) || undefined}
                        lastDelta={Number((item as any).Delta ?? (item as any).delta) || undefined}
                        lastGamma={Number((item as any).Gamma) || undefined}
                        lastTheta={Number((item as any).Theta) || undefined}
                        lastVega={Number((item as any).Vega) || undefined}
                        lastIV={Number((item as any)['Implied Volatility'] ?? (item as any).iv) || undefined}
                        lastPremium={Number((item as any)['Overall Premium'] || (item as any)['Delta Premium']) || undefined}
                      />
                    </td>
                  }
                />
              ))}
            </tbody>
                    </table>
          )}
        </div>
        <div
          ref={hScrollBottomRef}
          className={`shrink-0 overflow-x-auto overflow-y-hidden${simpleLayout ? ' min-w-0' : ''}`}
          style={{
            height: 14,
            borderTop: `1px solid ${themeColors.border}`,
            scrollbarWidth: 'thin',
            scrollbarColor: `${themeColors.border} ${themeColors.background}`,
          }}
          onScroll={(e) => setMainHorizontalScroll(e.currentTarget.scrollLeft)}
          title={language === 'ar' ? 'تمرير أفقي (أسفل)' : 'Horizontal scroll (bottom)'}
          aria-label={language === 'ar' ? 'تمرير أفقي أسفل الجدول' : 'Bottom horizontal scroll for table'}
        >
          <div ref={hScrollBottomInnerRef} style={{ height: 1, width: 1 }} />
        </div>
      </div>

      {/* Pagination */}
      {!simpleLayout && (
      <div style={dtChrome.foot}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {consolidatedView && (dataSource === 'stable' || dataSource === 'rtl_legacy') ? (
            <div className="text-sm" style={{ color: colors.textSecondary }}>
              {language === 'ar'
                ? `عرض مُجمَّع: ${filteredData.length} عقد ضمن الفلاتر (كل أسعار التنفيذ المعروضة).`
                : `Consolidated view: ${filteredData.length} contract rows aggregated by strike (all filtered strikes shown).`}
            </div>
          ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} style={dtChrome.pageNavBtn(currentPage === 1)}>
              {language === 'ar' ? 'الأول' : 'First'}
            </button>
            <button type="button" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} style={dtChrome.pageNavBtn(currentPage === 1)}>
              {language === 'ar' ? 'السابق' : 'Previous'}
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage >= Math.ceil(filteredData.length / itemsPerPage) || filteredData.length === 0}
              style={dtChrome.pageNavBtn(currentPage >= Math.ceil(filteredData.length / itemsPerPage) || filteredData.length === 0)}
            >
              {language === 'ar' ? 'التالي' : 'Next'}
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, Math.ceil(filteredData.length / itemsPerPage)))}
              disabled={currentPage >= Math.ceil(filteredData.length / itemsPerPage) || filteredData.length === 0}
              style={dtChrome.pageNavBtn(currentPage >= Math.ceil(filteredData.length / itemsPerPage) || filteredData.length === 0)}
            >
              {language === 'ar' ? 'الأخير' : 'Last'}
            </button>
          </div>
          )}
          
          {!(consolidatedView && (dataSource === 'stable' || dataSource === 'rtl_legacy')) && (
          <div className="text-sm" style={{ color: colors.textSecondary }}>
            {language === 'ar' ? 'عرض' : 'Showing'} {filteredData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredData.length)} {language === 'ar' ? 'من' : 'of'} {filteredData.length} {language === 'ar' ? 'سجل' : 'records'}
          </div>
          )}
        </div>
      </div>
      )}

      {/* Alerts Panel */}
      {showAlerts && (
        <div style={{ ...dtChrome.flyout, ...(language === 'ar' ? { left: 16, right: 'auto' as const } : { right: 16 }) }}>
          <div className="p-4" style={{ borderBottom: `1px solid ${colors.surfaceBorder}` }}>
            <div className="flex items-center justify-between">
              <h3 className="m-0" style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>
                Live Alerts ({alertNotifications.length})
              </h3>
              <button
                type="button"
                onClick={() => setShowAlerts(false)}
                style={{ ...dtChrome.sortBtn, marginLeft: 0, color: colors.textMuted, fontSize: 18 }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="p-2">
            {alertNotifications.length === 0 ? (
              <div className="text-center py-8" style={{ color: colors.textMuted, fontSize: 13 }}>
                No alerts yet. Changes will appear here!
              </div>
            ) : (
              <div className="space-y-2">
                {alertNotifications.map((alert) => (
                  <div
                    key={alert.id}
                    className="p-3 rounded-lg"
                    style={{
                      borderLeftWidth: 4,
                      borderLeftStyle: 'solid',
                      borderLeftColor:
                        alert.type === 'unusual' || alert.type === 'new_entry'
                          ? 'var(--flow, #b388ff)' /* alert category, not direction: flow lane purple */
                          : alert.type === 'premium'
                            ? colors.warning
                            : colors.info,
                      backgroundColor:
                        alert.type === 'unusual' || alert.type === 'new_entry'
                          ? 'rgba(179, 136, 255, 0.12)'
                          : alert.type === 'premium'
                            ? 'var(--warn-dim)'
                            : 'var(--acc-dim)',
                      border: `1px solid ${colors.surfaceBorder}`,
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: 6,
                              ...(alert.type === 'unusual' || alert.type === 'new_entry'
                                ? { backgroundColor: 'color-mix(in srgb, var(--flow, #b388ff) 30%, transparent)', color: 'var(--flow, #b388ff)' }
                                : alert.type === 'premium'
                                  ? { backgroundColor: 'color-mix(in srgb, var(--warn) 30%, transparent)', color: colors.warning }
                                  : { backgroundColor: 'color-mix(in srgb, var(--acc) 30%, transparent)', color: colors.info }),
                            }}
                          >
                            {String(alert.type ?? '').toUpperCase()}
                          </span>
                          <span className="text-xs" style={{ color: colors.textMuted }}>
                            {alert.timestamp.toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                          {alert.symbol}
                        </div>
                        <div className="text-sm" style={{ color: colors.textSecondary }}>
                          {alert.message}
                        </div>
                        {alert.oldPosition !== undefined && alert.newPosition !== undefined && (
                          <div className="text-xs mt-1" style={{ color: colors.textMuted }}>
                            Position: {alert.oldPosition + 1} → {alert.newPosition + 1}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alert Settings Panel */}
      {showAlertSettings && (
        <div style={{ ...dtChrome.flyout, ...(language === 'ar' ? { right: 16, left: 'auto' as const } : { left: 16 }) }}>
          <div className="p-4" style={{ borderBottom: `1px solid ${colors.surfaceBorder}` }}>
            <div className="flex items-center justify-between">
              <h3 className="m-0" style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>
                Alert Settings
              </h3>
              <button
                type="button"
                onClick={() => setShowAlertSettings(false)}
                style={{ ...dtChrome.sortBtn, marginLeft: 0, color: colors.textMuted, fontSize: 18 }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="p-4 space-y-4">
            {/* Premium Change Threshold */}
            <div>
              <label style={dtChrome.fieldLabel}>
                Premium Change Threshold (%)
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={alertSettings.premiumChangeThreshold}
                onChange={(e) => setAlertSettings(prev => ({ ...prev, premiumChangeThreshold: parseInt(e.target.value) || 50 }))}
                style={dtChrome.inputFull}
              />
              <p style={dtChrome.fieldHint}>
                Alert when premium fields change by this percentage or more
              </p>
            </div>

            {/* Position Change Threshold */}
            <div>
              <label style={dtChrome.fieldLabel}>
                Position Change Threshold
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={alertSettings.positionChangeThreshold}
                onChange={(e) => setAlertSettings(prev => ({ ...prev, positionChangeThreshold: parseInt(e.target.value) || 5 }))}
                style={dtChrome.inputFull}
              />
              <p style={dtChrome.fieldHint}>
                Alert when symbols move this many positions or more
              </p>
            </div>

            {/* Max Alerts History */}
            <div>
              <label style={dtChrome.fieldLabel}>
                Max Alerts History
              </label>
              <input
                type="number"
                min="10"
                max="1000"
                value={alertSettings.maxAlertsHistory}
                onChange={(e) => setAlertSettings(prev => ({ ...prev, maxAlertsHistory: parseInt(e.target.value) || 50 }))}
                style={dtChrome.inputFull}
              />
              <p style={dtChrome.fieldHint}>
                Maximum number of alerts to keep in history
              </p>
            </div>

            {/* Delta Premium Thresholds */}
            <div style={{ borderTop: `1px solid ${colors.surfaceBorder}`, paddingTop: 16 }}>
              <h4 style={dtChrome.sectionTitleSm}>Delta Premium Alerts</h4>
              
              <div className="space-y-3">
                <div>
                  <label style={dtChrome.fieldLabelTight}>
                    Delta Premium Above Threshold
                  </label>
                  <input
                    type="number"
                    value={alertSettings.deltaPremiumAboveThreshold}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, deltaPremiumAboveThreshold: parseFloat(e.target.value) || 1000 }))}
                    style={dtChrome.inputFull}
                  />
                  <p style={dtChrome.fieldHint}>
                    Alert when Delta Premium is above this value
                  </p>
                </div>
                
                <div>
                  <label style={dtChrome.fieldLabelTight}>
                    Delta Premium Below Threshold
                  </label>
                  <input
                    type="number"
                    value={alertSettings.deltaPremiumBelowThreshold}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, deltaPremiumBelowThreshold: parseFloat(e.target.value) || -1000 }))}
                    style={dtChrome.inputFull}
                  />
                  <p style={dtChrome.fieldHint}>
                    Alert when Delta Premium is below this value
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enableDeltaPremiumAlerts"
                    checked={alertSettings.enableDeltaPremiumAlerts}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, enableDeltaPremiumAlerts: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                  />
                  <label htmlFor="enableDeltaPremiumAlerts" style={{ marginLeft: 8, fontSize: 14, color: colors.text, cursor: 'pointer' }}>
                    Enable Delta Premium Alerts
                  </label>
                </div>
              </div>
            </div>

            {/* Delta Accumulated Premium Thresholds */}
            <div style={{ borderTop: `1px solid ${colors.surfaceBorder}`, paddingTop: 16 }}>
              <h4 style={dtChrome.sectionTitleSm}>Delta Accumulated Premium Alerts</h4>
              
              <div className="space-y-3">
                <div>
                  <label style={dtChrome.fieldLabelTight}>
                    Delta Accumulated Premium Above Threshold
                  </label>
                  <input
                    type="number"
                    value={alertSettings.deltaAccumulatedPremiumAboveThreshold}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, deltaAccumulatedPremiumAboveThreshold: parseFloat(e.target.value) || 10000 }))}
                    style={dtChrome.inputFull}
                  />
                  <p style={dtChrome.fieldHint}>
                    Alert when Delta Accumulated Premium is above this value
                  </p>
                </div>
                
                <div>
                  <label style={dtChrome.fieldLabelTight}>
                    Delta Accumulated Premium Below Threshold
                  </label>
                  <input
                    type="number"
                    value={alertSettings.deltaAccumulatedPremiumBelowThreshold}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, deltaAccumulatedPremiumBelowThreshold: parseFloat(e.target.value) || -10000 }))}
                    style={dtChrome.inputFull}
                  />
                  <p style={dtChrome.fieldHint}>
                    Alert when Delta Accumulated Premium is below this value
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enableDeltaAccumulatedPremiumAlerts"
                    checked={alertSettings.enableDeltaAccumulatedPremiumAlerts}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, enableDeltaAccumulatedPremiumAlerts: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                  />
                  <label htmlFor="enableDeltaAccumulatedPremiumAlerts" style={{ marginLeft: 8, fontSize: 14, color: colors.text, cursor: 'pointer' }}>
                    Enable Delta Accumulated Premium Alerts
                  </label>
                </div>
              </div>

              {/* Delta (Ask - Bid) Alerts */}
              <div className="space-y-4">
                <h4 style={dtChrome.sectionTitle}>
                  Delta (Ask - Bid) Alerts
                </h4>
                
                <div>
                  <label style={dtChrome.fieldLabelTight}>
                    Delta (Ask - Bid) Above Threshold
                  </label>
                  <input
                    type="number"
                    value={alertSettings.deltaAskBidAboveThreshold}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, deltaAskBidAboveThreshold: parseFloat(e.target.value) || 100 }))}
                    style={dtChrome.inputFull}
                  />
                  <p style={dtChrome.fieldHint}>
                    Alert when Delta (Ask - Bid) is above this value
                  </p>
                </div>
                
                <div>
                  <label style={dtChrome.fieldLabelTight}>
                    Delta (Ask - Bid) Below Threshold
                  </label>
                  <input
                    type="number"
                    value={alertSettings.deltaAskBidBelowThreshold}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, deltaAskBidBelowThreshold: parseFloat(e.target.value) || -100 }))}
                    style={dtChrome.inputFull}
                  />
                  <p style={dtChrome.fieldHint}>
                    Alert when Delta (Ask - Bid) is below this value
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enableDeltaAskBidAlerts"
                    checked={alertSettings.enableDeltaAskBidAlerts}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, enableDeltaAskBidAlerts: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                  />
                  <label htmlFor="enableDeltaAskBidAlerts" style={{ marginLeft: 8, fontSize: 14, color: colors.text, cursor: 'pointer' }}>
                    Enable Delta (Ask - Bid) Alerts
                  </label>
                </div>
              </div>

              {/* Delta Accumulated Alerts */}
              <div className="space-y-4">
                <h4 style={dtChrome.sectionTitle}>
                  Delta Accumulated Alerts
                </h4>
                
                <div>
                  <label style={dtChrome.fieldLabelTight}>
                    Delta Accumulated Above Threshold
                  </label>
                  <input
                    type="number"
                    value={alertSettings.deltaAccumulatedAboveThreshold}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, deltaAccumulatedAboveThreshold: parseFloat(e.target.value) || 1000 }))}
                    style={dtChrome.inputFull}
                  />
                  <p style={dtChrome.fieldHint}>
                    Alert when Delta Accumulated is above this value
                  </p>
                </div>
                
                <div>
                  <label style={dtChrome.fieldLabelTight}>
                    Delta Accumulated Below Threshold
                  </label>
                  <input
                    type="number"
                    value={alertSettings.deltaAccumulatedBelowThreshold}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, deltaAccumulatedBelowThreshold: parseFloat(e.target.value) || -1000 }))}
                    style={dtChrome.inputFull}
                  />
                  <p style={dtChrome.fieldHint}>
                    Alert when Delta Accumulated is below this value
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="enableDeltaAccumulatedAlerts"
                    checked={alertSettings.enableDeltaAccumulatedAlerts}
                    onChange={(e) => setAlertSettings(prev => ({ ...prev, enableDeltaAccumulatedAlerts: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                  />
                  <label htmlFor="enableDeltaAccumulatedAlerts" style={{ marginLeft: 8, fontSize: 14, color: colors.text, cursor: 'pointer' }}>
                    Enable Delta Accumulated Alerts
                  </label>
                </div>
              </div>
            </div>

            {/* Alert Type Toggles */}
            <div className="space-y-2">
              <label style={dtChrome.fieldLabel}>
                Alert Types
              </label>
              
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: colors.textSecondary }}>Premium Changes</span>
                <input
                  type="checkbox"
                  checked={alertSettings.enablePremiumAlerts}
                  onChange={(e) => setAlertSettings(prev => ({ ...prev, enablePremiumAlerts: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: colors.textSecondary }}>Position Changes</span>
                <input
                  type="checkbox"
                  checked={alertSettings.enablePositionAlerts}
                  onChange={(e) => setAlertSettings(prev => ({ ...prev, enablePositionAlerts: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: colors.textSecondary }}>New Top 10 Entries</span>
                <input
                  type="checkbox"
                  checked={alertSettings.enableNewEntryAlerts}
                  onChange={(e) => setAlertSettings(prev => ({ ...prev, enableNewEntryAlerts: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm" style={{ color: colors.textSecondary }}>Unusual Changes</span>
                <input
                  type="checkbox"
                  checked={alertSettings.enableUnusualAlerts}
                  onChange={(e) => setAlertSettings(prev => ({ ...prev, enableUnusualAlerts: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: colors.primary, cursor: 'pointer' }}
                />
              </div>
            </div>

            {/* Sound Selection */}
            <div className="space-y-2">
              <label style={dtChrome.fieldLabel}>
                Sound Selection
              </label>
              
              <div>
                <label style={dtChrome.fieldLabelXs}>
                  Alert Sound
                </label>
                <select
                  value={alertSettings.selectedAlertSound}
                  onChange={(e) => setAlertSettings(prev => ({ ...prev, selectedAlertSound: e.target.value }))}
                  style={dtChrome.inputFullXs}
                >
                  {ALERT_SOUNDS.map(sound => (
                    <option key={sound.id} value={sound.id}>
                      {sound.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={dtChrome.fieldLabelXs}>
                  Notification Sound
                </label>
                <select
                  value={alertSettings.selectedNotificationSound}
                  onChange={(e) => setAlertSettings(prev => ({ ...prev, selectedNotificationSound: e.target.value }))}
                  style={dtChrome.inputFullXs}
                >
                  {ALERT_SOUNDS.map(sound => (
                    <option key={sound.id} value={sound.id}>
                      {sound.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Sound Volume Controls */}
            <div className="space-y-2">
              <label style={dtChrome.fieldLabel}>
                Sound Volume
              </label>
              
              <div>
                <label style={dtChrome.fieldLabelXs}>
                  Alert Sound Volume: {Math.round(alertSettings.alertSoundVolume * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={alertSettings.alertSoundVolume}
                  onChange={(e) => setAlertSettings(prev => ({ ...prev, alertSoundVolume: parseFloat(e.target.value) }))}
                  style={dtChrome.rangeTrack}
                />
              </div>
              
              <div>
                <label style={dtChrome.fieldLabelXs}>
                  Notification Sound Volume: {Math.round(alertSettings.notificationSoundVolume * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={alertSettings.notificationSoundVolume}
                  onChange={(e) => setAlertSettings(prev => ({ ...prev, notificationSoundVolume: parseFloat(e.target.value) }))}
                  style={dtChrome.rangeTrack}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-4" style={{ borderTop: `1px solid ${colors.surfaceBorder}` }}>
              <button type="button" onClick={clearAlerts} style={{ ...dtChrome.lgBtn(colors.error, colors.textInverse), flex: 1 }}>
                Clear All Alerts
              </button>
              <button type="button" onClick={() => setShowAlertSettings(false)} style={{ ...dtChrome.pageNavBtn(false), flex: 1 }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {columnPickerOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="presentation"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 6000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              backgroundColor: 'color-mix(in srgb, var(--void) 60%, transparent)',
            }}
            onClick={() => setColumnPickerOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="data-table-column-modal-title"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(960px, calc(100vw - 32px))',
                maxHeight: 'min(88vh, 920px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 'var(--radius)',
                border: `1px solid ${colors.surfaceBorder}`,
                backgroundColor: colors.surface,
                boxShadow: 'var(--lift-3)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '12px 16px',
                  borderBottom: `1px solid ${colors.surfaceBorder}`,
                  flexShrink: 0,
                }}
              >
                <h2 id="data-table-column-modal-title" className="m-0" style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>
                  {language === 'ar' ? 'تخصيص الأعمدة' : 'Column customization'}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button type="button" onClick={resetToDefault} style={dtChrome.outlineBtn(false)}>
                    {language === 'ar' ? 'إعادة الافتراضي' : 'Reset columns'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setColumnPickerOpen(false)}
                    style={dtChrome.lgBtn(colors.primary, colors.textInverse)}
                  >
                    {language === 'ar' ? 'تم' : 'Done'}
                  </button>
                </div>
              </div>
              <div style={{ padding: 16, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                <div
                  className="grid gap-3"
                  style={{
                    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  }}
                >
                  {effectiveColumnConfig.map((col) => (
                    <div
                      key={col.key}
                      className="flex items-center gap-2 rounded-lg p-3"
                      style={{ border: `1px solid ${colors.surfaceBorder}`, backgroundColor: colors.background }}
                    >
                      <input
                        type="checkbox"
                        checked={col.visible}
                        onChange={() => toggleColumnVisibility(col.key)}
                        className="rounded"
                        style={{ accentColor: colors.primary }}
                      />
                      <span className="min-w-0 flex-1 break-words text-sm" style={{ color: colors.text }} title={col.label}>
                        {col.label}
                      </span>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          onClick={() => moveColumn(col.key, 'up')}
                          style={dtChrome.smOutlineDisabled(col.order === 1)}
                          disabled={col.order === 1}
                          aria-label={language === 'ar' ? 'أعلى' : 'Move up'}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => moveColumn(col.key, 'down')}
                          style={dtChrome.smOutlineDisabled(col.order === effectiveColumnConfig.length)}
                          disabled={col.order === effectiveColumnConfig.length}
                          aria-label={language === 'ar' ? 'أسفل' : 'Move down'}
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {advancedFilterModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            role="presentation"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 6050,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              backgroundColor: 'color-mix(in srgb, var(--void) 60%, transparent)',
            }}
            onClick={() => setAdvancedFilterModalOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="data-table-advanced-modal-title"
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(720px, calc(100vw - 32px))',
                maxHeight: 'min(90vh, 720px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 'var(--radius)',
                border: `1px solid ${colors.surfaceBorder}`,
                backgroundColor: colors.surface,
                boxShadow: 'var(--lift-3)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  padding: '12px 16px',
                  borderBottom: `1px solid ${colors.surfaceBorder}`,
                  flexShrink: 0,
                }}
              >
                <h2 id="data-table-advanced-modal-title" className="m-0" style={{ color: colors.text, fontSize: 14, fontWeight: 600 }}>
                  {language === 'ar' ? 'فلاتر متقدمة' : 'Advanced filters'}
                </h2>
                <p className="m-0 text-sm" style={{ color: colors.textMuted, lineHeight: 1.45 }}>
                  {language === 'ar'
                    ? 'عدّل الشروط ثم اضغط تم لتطبيقها على الجدول. إلغاء يتجاهل التغييرات. يمكنك أيضاً مسح كل المرشحات من زر «مسح المرشحات» في لوحة المرشحات.'
                    : 'Edit conditions, then tap Done to apply them to the table. Cancel discards changes. You can also clear everything with Clear filters in the filter panel.'}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {advancedFilterDraft.some((g) => g.conditions.length > 0) && (
                    <span style={dtChrome.chip}>
                      {language === 'ar' ? 'مسودة — مجموعات ذات شروط:' : 'Draft — groups with conditions:'}{' '}
                      {advancedFilterDraft.filter((g) => g.conditions.length > 0).length}
                    </span>
                  )}
                  <span className="text-sm" style={{ color: colors.textMuted }}>
                    {language === 'ar' ? 'حجم مجموعة البيانات:' : 'Dataset size:'} {data.length.toLocaleString()}
                  </span>
                </div>
              </div>

              <div style={{ padding: 16, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setAdvancedFilterDraft((prev) => [
                          ...prev,
                          { id: `group-${Date.now()}`, conditions: [], logicalOperator: 'AND' },
                        ])
                      }
                      style={dtChrome.lgBtn(colors.primary, colors.textInverse)}
                    >
                      {language === 'ar' ? 'إضافة مجموعة' : 'Add filter group'}
                    </button>
                    {advancedFilterDraft.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setAdvancedFilterDraft([])}
                        style={dtChrome.lgBtn(colors.error, colors.textInverse)}
                      >
                        {language === 'ar' ? 'مسح المسودة' : 'Clear draft'}
                      </button>
                    )}
                  </div>
                </div>

                {advancedFilterDraft.map((group, groupIndex) => (
                  <div key={group.id} style={{ ...dtChrome.subPanel, marginBottom: 12 }}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={group.logicalOperator}
                          onChange={(e) =>
                            setAdvancedFilterDraft((prev) =>
                              prev.map((g) =>
                                g.id === group.id ? { ...g, logicalOperator: e.target.value as 'AND' | 'OR' } : g,
                              ),
                            )
                          }
                          style={{ ...dtChrome.select, padding: '6px 10px', fontSize: 12 }}
                        >
                          <option value="AND">AND</option>
                          <option value="OR">OR</option>
                        </select>
                        <span className="text-sm font-medium" style={{ color: colors.text }}>
                          {language === 'ar' ? 'مجموعة' : 'Group'} {groupIndex + 1}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            setAdvancedFilterDraft((prev) =>
                              prev.map((g) =>
                                g.id === group.id
                                  ? {
                                      ...g,
                                      conditions: [
                                        ...g.conditions,
                                        {
                                          id: `condition-${Date.now()}`,
                                          field: 'Underlying',
                                          operator: 'equals',
                                          value: '',
                                          logicalOperator: 'AND',
                                        },
                                      ],
                                    }
                                  : g,
                              ),
                            )
                          }
                          style={{ ...dtChrome.lgBtn(colors.primary, colors.textInverse), padding: '6px 12px', fontSize: 13 }}
                        >
                          {language === 'ar' ? 'إضافة شرط' : 'Add condition'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAdvancedFilterDraft((prev) => prev.filter((g) => g.id !== group.id))}
                          style={{ ...dtChrome.lgBtn(colors.error, colors.textInverse), padding: '6px 12px', fontSize: 13 }}
                        >
                          {language === 'ar' ? 'حذف المجموعة' : 'Remove group'}
                        </button>
                      </div>
                    </div>

                    {group.conditions.map((condition) => (
                      <div key={condition.id} className="flex flex-wrap items-center gap-2 mb-2">
                        <select
                          value={condition.field as string}
                          onChange={(e) =>
                            setAdvancedFilterDraft((prev) =>
                              prev.map((g) =>
                                g.id === group.id
                                  ? {
                                      ...g,
                                      conditions: g.conditions.map((c) =>
                                        c.id === condition.id ? { ...c, field: e.target.value } : c,
                                      ),
                                    }
                                  : g,
                              ),
                            )
                          }
                          style={{ ...dtChrome.select, padding: '6px 10px', fontSize: 12, flex: '1 1 120px', minWidth: 100 }}
                        >
                          {data.length > 0 ? Object.keys(data[0]).map((fieldKey) => (
                            <option key={fieldKey} value={fieldKey}>
                              {fieldKey}
                            </option>
                          )) : []}
                        </select>
                        <select
                          value={condition.operator}
                          onChange={(e) =>
                            setAdvancedFilterDraft((prev) =>
                              prev.map((g) =>
                                g.id === group.id
                                  ? {
                                      ...g,
                                      conditions: g.conditions.map((c) =>
                                        c.id === condition.id
                                          ? { ...c, operator: e.target.value as FilterCondition['operator'] }
                                          : c,
                                      ),
                                    }
                                  : g,
                              ),
                            )
                          }
                          style={{ ...dtChrome.select, padding: '6px 10px', fontSize: 12, minWidth: 120 }}
                        >
                          <option value="equals">equals</option>
                          <option value="not_equals">not equals</option>
                          <option value="contains">contains</option>
                          <option value="not_contains">not contains</option>
                          <option value="greater">greater than</option>
                          <option value="less">less than</option>
                          <option value="between">between</option>
                          <option value="in">in</option>
                          <option value="is_null">is null</option>
                          <option value="is_not_null">is not null</option>
                        </select>
                        <input
                          type="text"
                          value={condition.value as string}
                          onChange={(e) =>
                            setAdvancedFilterDraft((prev) =>
                              prev.map((g) =>
                                g.id === group.id
                                  ? {
                                      ...g,
                                      conditions: g.conditions.map((c) =>
                                        c.id === condition.id ? { ...c, value: e.target.value } : c,
                                      ),
                                    }
                                  : g,
                              ),
                            )
                          }
                          placeholder={language === 'ar' ? 'القيمة' : 'Value'}
                          style={{ ...dtChrome.inputSm, flex: '1 1 140px', width: 'auto', minWidth: 80 }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setAdvancedFilterDraft((prev) =>
                              prev.map((g) =>
                                g.id === group.id
                                  ? {
                                      ...g,
                                      conditions: g.conditions.filter((c) => c.id !== condition.id),
                                    }
                                  : g,
                              ),
                            )
                          }
                          style={{ ...dtChrome.lgBtn(colors.error, colors.textInverse), padding: '6px 10px', fontSize: 14, lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div
                className="flex flex-wrap gap-2 justify-end"
                style={{
                  padding: '12px 18px',
                  borderTop: `1px solid ${colors.surfaceBorder}`,
                  flexShrink: 0,
                  backgroundColor: colors.background,
                }}
              >
                <button type="button" onClick={() => setAdvancedFilterModalOpen(false)} style={dtChrome.outlineBtn(false)}>
                  {language === 'ar' ? 'إلغاء' : 'Cancel'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFilterGroups(cloneFilterGroups(advancedFilterDraft));
                    setUseAdvancedFiltering(advancedFilterDraft.some((g) => g.conditions.length > 0));
                    setAdvancedFilterModalOpen(false);
                  }}
                  style={dtChrome.lgBtn(colors.primary, colors.textInverse)}
                >
                  {language === 'ar' ? 'تم' : 'Done'}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default DataTable;